/**
 * @module KioskPage
 * @description Lager-Terminal Kiosk — 3-Screen-Erlebnis (Idle → Erkennung → Bestätigung).
 *   Großformatig, touch-optimiert, aus 2m lesbar.
 *
 *   Zugang (#1816): ein GEKOPPELTES Terminal weist sich mit seinem Device-Token
 *   aus und ist ohne PIN scanbereit — die Registrierung regelt den Zugang. Die
 *   PIN-Abfrage bleibt für nicht registrierte Geräte als Notweg (Ersatztablet).
 *
 *   Die PIN hat seither eine zweite Rolle: sie ist der Betriebsschalter. Über
 *   „Terminal sperren" wird das Gerät vorübergehend außer Betrieb genommen
 *   (Umbau, Wartung, kein Stempeln nach Feierabend); entsperrt wird nur mit PIN
 *   oder aus der Ferne über die Terminal-Liste. Der Sperrzustand liegt
 *   SERVERSEITIG — ein Browser-Flag wäre per Neuladen umgehbar.
 *
 *   Einrichtung (#1820): ein noch nicht gekoppeltes Gerät bekommt hier den
 *   Kopplungscode-Schirm, die PIN steht darunter als Notweg. Beides sind
 *   ZUSTÄNDE DIESER SEITE — die Kiosk-App läuft mit fest eingestellter
 *   Adresse, ein Sprung auf /geraet-einrichten käme unter Umständen nicht
 *   zurück. Eingelöst wird über utils/kioskDevice > koppleGeraet(), dieselbe
 *   Funktion wie auf der Desktop-Einrichtungsseite.
 * @route /kiosk (public)
 * @since v2.195.0
 * @updated v2.874.0 — Kopplung direkt am Terminal, ohne Adresswechsel (#1820)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { geraetHeaders, getGeraetToken, clearGeraetToken, koppleGeraet } from '../utils/kioskDevice';

const API = import.meta.env.VITE_API_URL || '';

// ════════════════════════════════════════════════════════════════
//  CONSTANTS
// ════════════════════════════════════════════════════════════════
const TIMEOUTS = {
  auswahl: 15000,
  mittag: 60000,
  bestaetigung: 5000,
  feierabend: 8000,
  fehler: 5000,
  grund: 60000,
};

// #1824 Teil B — Haltedauer für den Notausgang. Lang genug, dass ein Tipper
// oder ein gestreiftes Logo ihn nicht auslöst, kurz genug zum Erklären.
const LOGO_DRUCK_MS = 3000;

const COLORS = {
  green: { bg: '#15803d', text: '#ffffff', light: '#dcfce7', border: '#86efac' },
  red: { bg: '#dc2626', text: '#ffffff', light: '#fee2e2', border: '#fca5a5' },
  amber: { bg: '#d97706', text: '#ffffff', light: '#fef3c7', border: '#fcd34d' },
  blue: { bg: '#338edc', text: '#ffffff', light: '#EBF4FD', border: '#93c5fd' },
};

const AVATAR_BG = ['#EBF4FD', '#E1F5EE', '#FAEEDA', '#F3E8FF'];
const AVATAR_FG = ['#0C447C', '#0F6E56', '#854F0B', '#6D28D9'];

// ════════════════════════════════════════════════════════════════
//  SOUND
// ════════════════════════════════════════════════════════════════
let audioCtx = null;
function getAudioCtx() {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return audioCtx;
}
function playSound(typ) {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    if (typ === 'ein') {
      osc.frequency.value = 880; gain.gain.value = 0.3;
      osc.start(); setTimeout(() => osc.stop(), 150);
    } else if (typ === 'aus') {
      osc.frequency.value = 440; gain.gain.value = 0.3;
      osc.start(); setTimeout(() => osc.stop(), 200);
    } else if (typ === 'fehler') {
      osc.frequency.value = 220; osc.type = 'square'; gain.gain.value = 0.2;
      osc.start(); setTimeout(() => osc.stop(), 400);
    }
  } catch { /* Audio not available */ }
}

// ════════════════════════════════════════════════════════════════
//  OFFLINE QUEUE
// ════════════════════════════════════════════════════════════════
// #1817 — Ein Eintrag OHNE Aktion ist keine Buchung, sondern eine Status-
// Abfrage: der Server antwortet darauf mit 200 und schreibt nichts. Genau so
// verschwanden Stempel still. `aktion` ist deshalb Pflicht — wer keine hat,
// darf nicht puffern, sondern muss den Fehlschlag zeigen.
//
// NUR der Ausweis darf gepuffert werden, NIE die PIN (#1817 Nachtrag): das
// Terminal ist ein GETEILTES Geraet, eine gepufferte PIN laege dort als
// auslesbares Identitaetsmerkmal eines einzelnen Mitarbeiters im
// localStorage. Der Ausweis ist unbedenklich, weil er auch online der
// alleinige Nachweis ist — `/api/stempel/scan` kennt keine PIN-Pruefung.
function queueStempel(ident, aktion, extras = {}) {
  if (!aktion || !ident?.barcode) return null;
  const queue = ladeQueue();
  const eintrag = {
    barcode: ident.barcode,
    aktion,
    ...extras,                      // pause_grund, grund_kategorie
    // Der Schluessel entsteht EINMAL beim Puffern und bleibt ueber alle
    // Sendeversuche gleich — daran erkennt der Server den Wiederholversuch
    // und bucht nicht doppelt.
    client_key: neuerClientKey(),
    // Die Zeit, zu der der Mitarbeiter am Terminal stand. Der Server bucht
    // damit rueckwirkend statt mit der Zeit des Nachsendens.
    offline_zeitpunkt: new Date().toISOString(),
    versuche: 0,
    abgelehnt: null,
  };
  queue.push(eintrag);
  speichereQueue(queue);
  return eintrag;
}

function neuerClientKey() {
  try {
    if (crypto?.randomUUID) return crypto.randomUUID();
  } catch { /* aeltere WebView ohne crypto.randomUUID */ }
  return `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function ladeQueue() {
  try {
    const roh = JSON.parse(localStorage.getItem('stempel_queue') || '[]');
    // Zwei Altlasten werden hier aussortiert:
    //  - Eintraege OHNE Aktion (vor #1817): waren nie buchbar und wuerden es
    //    auch jetzt nicht — sie wuerden ewig als „haengend" gemeldet.
    //  - Eintraege MIT PIN (kurzzeitig in 2.872.0): die PIN darf auf einem
    //    geteilten Terminal nicht liegenbleiben. Sie hier zu verwerfen ist der
    //    einzige Weg, sie auch von Geraeten zu bekommen, die schon gepuffert
    //    haben — ein blosses Entfernen des Schreibpfads liesse sie stehen.
    if (!Array.isArray(roh)) return [];
    const sauber = roh.filter(e => e && e.aktion && e.barcode && !e.pin);
    // Zurueckschreiben, sobald etwas wegfaellt: Filtern beim Lesen allein
    // wuerde die PIN im localStorage stehen lassen — genau das soll weg.
    if (sauber.length !== roh.length) speichereQueue(sauber);
    return sauber;
  } catch { return []; }
}

function speichereQueue(queue) {
  localStorage.setItem('stempel_queue', JSON.stringify(queue));
}

function queueUebersicht() {
  const queue = ladeQueue();
  return {
    offen: queue.filter(e => !e.abgelehnt).length,
    abgelehnt: queue.filter(e => e.abgelehnt).length,
    // Aelteste offene Buchung — daran haengt die Warnung „haengt fest".
    aeltester: queue.length ? queue[0].offline_zeitpunkt : null,
  };
}
function getKioskAuth() {
  // Der Device-Token reist IMMER mit (#1816): er ist seit v2.871.0 ein
  // vollwertiger Zugangsnachweis und gleichzeitig das, woran der Server die
  // Herkunft der Buchung erkennt (#1814). PIN-JWT und Legacy-Key bleiben
  // zusätzlich erhalten — ein Ersatztablet hat keinen Device-Token.
  const headers = { ...geraetHeaders() };
  const token = localStorage.getItem('kiosk_token');
  if (token) headers['x-kiosk-token'] = token;
  else {
    const key = localStorage.getItem('stempel_api_key');
    if (key) headers['x-stempel-key'] = key;
  }
  return headers;
}

// #1817 — Ein Eintrag verlaesst die Queue AUSSCHLIESSLICH bei positiv
// bestaetigter Buchung (`erfolg === true`), nie per Ausschluss ueber `res.ok`.
// Das war der Kern des Fehlers: der Server quittiert eine Status-Abfrage mit
// 200, und `res.ok` deutete das als „gebucht" und loeschte die Arbeitszeit.
// Dieselbe Lehre wie #1812, hier mit echtem Datenverlust statt nur falscher
// Meldung.
//
// Rueckgabe: { gebucht, offen, abgelehnt } fuer die Anzeige am Terminal.
async function syncQueue() {
  const queue = ladeQueue();
  if (!queue.length) return { gebucht: 0, offen: 0, abgelehnt: 0 };

  const bleibt = [];
  let gebucht = 0;
  let netzAus = false;

  for (const eintrag of queue) {
    // Nach einem Netzwerkfehler die restlichen Eintraege gar nicht erst
    // versuchen — sie wuerden nur ihren Versuchszaehler hochtreiben.
    if (netzAus) { bleibt.push(eintrag); continue; }

    // Immer der Ausweis-Weg: die Queue enthaelt seit dem #1817-Nachtrag
    // ausschliesslich Barcode-Eintraege (die PIN wird nicht mehr gepuffert).
    try {
      const res = await fetch(`${API}/api/stempel/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getKioskAuth() },
        body: JSON.stringify(eintrag),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.erfolg === true) { gebucht++; continue; }

      const versuche = (eintrag.versuche || 0) + 1;

      // Der Server hat geantwortet und die Buchung inhaltlich abgelehnt
      // (Regelverstoss, unbekannter Barcode, gesperrtes Terminal ...). Ein
      // Wiederholen aendert daran nichts, aber GELOESCHT wird trotzdem nicht:
      // ein abgelehnter Stempel ist Arbeitszeit, die jemand ansehen muss (AK5).
      const endgueltig = res.status === 404 || res.status === 403 || res.status === 423
        || data.rule_blocked === true || (res.status === 400 && !data.erfolg);

      bleibt.push({
        ...eintrag,
        versuche,
        abgelehnt: endgueltig
          ? (data.rule_meldung || data.message || data.error || `Abgelehnt (${res.status})`)
          : eintrag.abgelehnt,
      });
    } catch {
      // Kein Netz — Eintrag unveraendert behalten, Zaehler nicht hochtreiben.
      netzAus = true;
      bleibt.push(eintrag);
    }
  }

  speichereQueue(bleibt);
  return {
    gebucht,
    offen: bleibt.filter(e => !e.abgelehnt).length,
    abgelehnt: bleibt.filter(e => e.abgelehnt).length,
  };
}

// ════════════════════════════════════════════════════════════════
//  FORMAT HELPERS
// ════════════════════════════════════════════════════════════════
function formatMinuten(min) {
  const h = Math.floor(Math.abs(min) / 60);
  const m = Math.round(Math.abs(min) % 60);
  const sign = min < 0 ? '-' : '';
  return h > 0 ? `${sign}${h}h ${m.toString().padStart(2, '0')}min` : `${sign}${m}min`;
}
function formatUhrzeit(ts) {
  return new Date(ts).toLocaleTimeString('de-AT', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Vienna',
  });
}
function formatSollStunden(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

// ════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ════════════════════════════════════════════════════════════════
export default function KioskPage() {
  const { t, setLanguage } = useLanguage();

  // Kiosk immer auf Deutsch (Browsersprache ignorieren)
  useEffect(() => { setLanguage('de'); }, [setLanguage]);

  // ─── Kiosk Auth ───
  const [authenticated, setAuthenticated] = useState(() => {
    // Legacy: accept ?key= URL param
    const params = new URLSearchParams(window.location.search);
    const urlKey = params.get('key');
    if (urlKey) {
      localStorage.setItem('stempel_api_key', urlKey);
      window.history.replaceState({}, '', '/kiosk');
      return true;
    }
    return !!(localStorage.getItem('kiosk_token') || localStorage.getItem('stempel_api_key'));
  });
  const [authChecked, setAuthChecked] = useState(false);

  // ─── Registriertes Terminal (#1816) ───
  // `geraet` ist die Wahrheit über den Zugang, `gesperrt` die über den Betrieb.
  // Beides kommt vom Server, nicht aus dem localStorage: der Token beweist nur,
  // dass das Gerät einmal gekoppelt WAR — Widerruf und Sperre wirken sofort.
  const [geraet, setGeraet] = useState(null);
  const [gesperrt, setGesperrt] = useState(false);
  const [entsperrPin, setEntsperrPin] = useState('');
  const [entsperrError, setEntsperrError] = useState(null);
  const [entsperrShake, setEntsperrShake] = useState(false);
  const [entsperrLoading, setEntsperrLoading] = useState(false);
  // Das Nummernfeld des Sperrschirms ist der ENTSPERR-Weg, nicht die
  // Bestaetigung des Sperrens. Stand es sofort da, tippte wer gerade gesperrt
  // hatte reflexhaft die PIN und hob die Sperre in derselben Bewegung wieder
  // auf — das sah aus, als haette das Sperren nicht gewirkt.
  const [entsperrModus, setEntsperrModus] = useState(false);

  // ─── Notausgang „Dieses Gerät neu koppeln" (#1824 Teil B) ───
  // Der Weg aus einem festgefahrenen Tablet heraus, ohne Browser-Einstellungen:
  // In der Kiosk-App lassen sich die Website-Daten nicht löschen, und ein noch
  // gültiges Alt-Token hielt den Scan-Schirm am Leben.
  //
  // Zwei Stufen, nicht eine: erst die PIN, dann die ausdrückliche Bestätigung.
  // Der Griff ist absichtlich unauffällig (langer Druck aufs Logo), damit ihn
  // niemand beim Scannen streift; die Entkopplung selbst darf trotzdem nicht
  // aus einem einzigen Tipper folgen (#1816-Fix: ein Schirm darf die Rücknahme
  // dessen, was man gerade getan hat, nicht als Reflex anbieten).
  const [notausgang, setNotausgang] = useState(null); // null | 'pin' | 'bestaetigen'
  const [notausgangPin, setNotausgangPin] = useState('');
  const [notausgangError, setNotausgangError] = useState(null);
  const [notausgangShake, setNotausgangShake] = useState(false);
  const [notausgangLoading, setNotausgangLoading] = useState(false);
  const logoDruckRef = useRef(null);

  // ─── State ───
  const [state, setState] = useState('idle');
  const [scanData, setScanData] = useState(null);
  const [error, setError] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [anwesendCount, setAnwesendCount] = useState({ anwesend: 0, gesamt: 0 });
  const [processing, setProcessing] = useState(false);
  const processingRef = useRef(false);
  // #1817 — was noch ungebucht in der Warteschlange liegt. Wird angezeigt, nicht
  // nur gezaehlt: eine Buchung, die niemand sieht, ist so gut wie verloren.
  const [queueStatus, setQueueStatus] = useState(() => queueUebersicht());
  const [pinMode, setPinMode] = useState(false);
  const [pinDigits, setPinDigits] = useState('');
  const [pinShake, setPinShake] = useState(false);
  const [nochWartenMin, setNochWartenMin] = useState(0);
  const [kannBeenden, setKannBeenden] = useState(false);
  const [mittagElapsed, setMittagElapsed] = useState(0);
  const [mittagMindest, setMittagMindest] = useState(30);
  const [countdownKey, setCountdownKey] = useState(0);
  const [countdownMs, setCountdownMs] = useState(0);
  const [adminPin, setAdminPin] = useState('');
  const [adminPinError, setAdminPinError] = useState(null);
  const [adminPinShake, setAdminPinShake] = useState(false);
  const [adminPinLoading, setAdminPinLoading] = useState(false);
  // ─── Einrichtung am Terminal selbst (#1820) ───
  // Der Kopplungscode ist der Regelweg, die Terminal-PIN der Notweg. Beide
  // sind Zustände DIESER Seite: die Kiosk-App hat eine fest eingestellte
  // Adresse, ein Wechsel auf /geraet-einrichten käme unter Umständen nicht
  // zurück.
  const [setupModus, setSetupModus] = useState('code'); // 'code' | 'pin'
  const [koppelCode, setKoppelCode] = useState('');
  const [koppelError, setKoppelError] = useState(null);
  const [koppelShake, setKoppelShake] = useState(false);
  const [koppelLoading, setKoppelLoading] = useState(false);
  // Pause-Grund-Eingabe (Regel 2)
  const [grundAktion, setGrundAktion] = useState(null); // 'kurze_pause' | 'mittagspause'
  const [grundWarnung, setGrundWarnung] = useState(null);
  const [grundMinuten, setGrundMinuten] = useState(null);
  const [grundLimit, setGrundLimit] = useState(null);
  const [grundManuell, setGrundManuell] = useState('');
  const [grundManuellMode, setGrundManuellMode] = useState(false);
  // Regel-Block-Meldung (#817 — Limitation anzeigen, nicht still blockieren)
  const [regelBlock, setRegelBlock] = useState(null); // { code, meldung, details, aktion }
  const voiceEnabledRef = useRef(false);

  // ─── Refs ───
  const scanIdentRef = useRef(null);
  // #817 — Stable copy of barcode/pin, only cleared in goIdle.
  // scanIdentRef gets reset whenever a new keystroke burst triggers processBuffer,
  // which breaks the second scan request during Grund-Eingabe (especially "Sonstiges"
  // text input where each keystroke feeds the HID scanner buffer).
  const currentIdentRef = useRef(null);
  const timeoutRef = useRef(null);
  const pinTimeoutRef = useRef(null);
  const queueSyncRef = useRef(null);
  // #1817 — Ausweis, der offline gescannt wurde und auf die Aktionsauswahl
  // wartet. Bewusst NUR der Barcode: die PIN wird nie zwischengespeichert.
  const offlineIdentRef = useRef(null);

  // ─── Clock tick ───
  useEffect(() => {
    const iv = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  // ─── Registrierung + Sperrzustand vom Server holen (#1816) ───
  // Wird auch gepollt: nur so erreicht eine Fernentsperrung aus der
  // Terminal-Liste das Tablet, ohne dass jemand in die Halle laufen muss (AK5).
  // Der Rückgabewert unterscheidet ZWEI Fälle, die vorher beide `null` waren und
  // deshalb gleich behandelt wurden: das ausdrückliche Nein des Servers
  // (`{kiosk:false}` auf einen MITGESENDETEN Token = Registrierung widerrufen)
  // und das Schweigen des Netzes (offline, 5xx). Nur das Nein darf wirken —
  // ein Netzausfall darf ein registriertes Terminal weder aussperren noch
  // stillschweigend entsperren, und ein weggeworfener Token wäre ohne einen
  // neuen Kopplungscode nicht wiederherstellbar.
  const ladeGeraetStatus = useCallback(async () => {
    if (!getGeraetToken()) return null;
    try {
      const res = await fetch(`${API}/api/kiosk-geraete/status`, { headers: geraetHeaders() });
      if (!res.ok) return null;
      const d = await res.json();
      if (!d.kiosk) {
        // EINE Auswertungsstelle des Widerrufs — sie liegt hier, weil hier die
        // Wahrheit ankommt: beim Laden UND beim 10-s-Abgleich. Der Token beweist
        // nur, dass das Gerät einmal gekoppelt WAR; sagt der Server Nein, endet
        // damit auch die Kiosk-Sitzung. Ohne das lief das Terminal mit dem noch
        // gültigen PIN-Token (30 Tage) unverändert weiter und der Widerruf war
        // wirkungslos.
        clearGeraetToken();
        localStorage.removeItem('kiosk_token');
        localStorage.removeItem('stempel_api_key');
        setGeraet(null);
        setGesperrt(false);
        setAuthenticated(false);
        return d;
      }
      setGeraet({ name: d.name, standort: d.standort });
      setGesperrt(!!d.gesperrt);
      return d;
    } catch {
      // Offline: die zuletzt bekannte Lage gilt weiter. Ein Netzausfall darf ein
      // registriertes Terminal weder aussperren noch stillschweigend entsperren.
      return null;
    }
  }, []);

  // ─── Verify kiosk token on mount ───
  useEffect(() => {
    const token = localStorage.getItem('kiosk_token');
    const legacyKey = localStorage.getItem('stempel_api_key');

    // Registriertes Gerät zuerst: es braucht keine PIN (AK1). Fällt die Prüfung
    // negativ aus (unbekannt/widerrufen), greift unverändert der PIN-Weg — eine
    // fehlende Registrierung darf niemanden aussperren (FAIL-DIRECTION #1809).
    ladeGeraetStatus().then(d => {
      if (d?.kiosk) { setAuthenticated(true); setAuthChecked(true); return; }
      // Widerrufen: `ladeGeraetStatus()` hat die Sitzung bereits beendet. Der
      // PIN-Weg darf sie hier nicht wiederbeleben — `token` stammt aus der Zeit
      // vor dem Widerruf und wäre serverseitig noch gültig.
      if (d && d.kiosk === false) { setAuthChecked(true); return; }
      if (token) {
        fetch(`${API}/api/kiosk/verify`, { headers: { 'x-kiosk-token': token } })
          .then(r => {
            if (!r.ok) { localStorage.removeItem('kiosk_token'); setAuthenticated(!!legacyKey); }
            setAuthChecked(true);
          })
          .catch(() => setAuthChecked(true));
      } else {
        setAuthChecked(true);
      }
    });
  }, []); // eslint-disable-line

  // ─── Sperrzustand nachführen (10 s) ───
  useEffect(() => {
    if (!geraet) return;
    const iv = setInterval(() => { ladeGeraetStatus(); }, 10000);
    return () => clearInterval(iv);
  }, [geraet, ladeGeraetStatus]);

  // ─── Load kiosk config (voice, etc.) ───
  useEffect(() => {
    if (!authenticated) return;
    fetch(`${API}/api/kiosk/config`, { headers: getKioskAuth() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) voiceEnabledRef.current = !!d.voice_enabled; })
      .catch(() => {});
  }, [authenticated]);

  const clockHM = clock.toLocaleTimeString('de-AT', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Vienna',
  });
  const clockS = clock.toLocaleTimeString('de-AT', {
    second: '2-digit', timeZone: 'Europe/Vienna',
  }).slice(-2);
  const dateStr = clock.toLocaleDateString('de-AT', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Europe/Vienna',
  });

  // ─── Scanner buffer for HID barcode reader ───
  const scanBufferRef = useRef('');
  const scanTimerRef = useRef(null);
  // Refs for stable event handler (avoid stale closures + effect re-registration)
  const handleBarcodeScanRef = useRef(null);
  const stateRef = useRef(state);
  const pinModeRef = useRef(pinMode);
  const authenticatedRef = useRef(authenticated);
  const gesperrtRef = useRef(gesperrt);
  const notausgangRef = useRef(notausgang);
  // Keep refs in sync with latest values (runs every render, no effect teardown)
  useEffect(() => { handleBarcodeScanRef.current = handleBarcodeScan; });
  useEffect(() => { stateRef.current = state; });
  useEffect(() => { pinModeRef.current = pinMode; });
  useEffect(() => { authenticatedRef.current = authenticated; });
  useEffect(() => { gesperrtRef.current = gesperrt; });
  useEffect(() => { notausgangRef.current = notausgang; });

  // ─── Fullscreen ───
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen().catch(() => {});
  }, []);

  // ─── Disable context menu + zoom for kiosk ───
  useEffect(() => {
    const prevent = e => e.preventDefault();
    document.addEventListener('contextmenu', prevent);
    // Disable pinch zoom
    const meta = document.querySelector('meta[name="viewport"]');
    const orig = meta?.content;
    if (meta) meta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
    return () => {
      document.removeEventListener('contextmenu', prevent);
      if (meta && orig) meta.content = orig;
    };
  }, []);

  // ─── Load ticker (just count, no names) ───
  const loadTicker = useCallback(async () => {
    if (!authenticated) return;
    try {
      const res = await fetch(`${API}/api/stempel/ticker`, {
        headers: getKioskAuth(),
      });
      if (res.ok) {
        const d = await res.json();
        setAnwesendCount({ anwesend: d.anwesend || 0, gesamt: d.gesamt || 0 });
      } else if (res.status === 401 && !getGeraetToken()) {
        // PIN-JWT abgelaufen. Ein registriertes Gerät ist hier ausgenommen: sein
        // Zugang hängt am Device-Token, nicht an diesem JWT — ein 401 aus einem
        // anderen Grund dürfte es nicht in den PIN-Schirm werfen (#1816).
        localStorage.removeItem('kiosk_token');
        setAuthenticated(!!localStorage.getItem('stempel_api_key'));
      }
    } catch { /* offline */ }
  }, [authenticated]);

  // ─── Initial load ───
  useEffect(() => { if (authenticated) loadTicker(); }, []); // eslint-disable-line

  // ─── Ticker polling (10s) + queue sync (30s) ───
  useEffect(() => {
    if (state !== 'idle') return;
    loadTicker();
    const iv = setInterval(loadTicker, 10000);
    // #1817 — nach jedem Sync die Anzeige nachziehen. Ohne das bliebe der
    // Zaehler auf dem Stand des letzten Renders stehen und ein nachgeholter
    // Stempel saehe weiterhin aus wie einer, der noch aussteht.
    const syncUndAnzeigen = () => { syncQueue().then(() => setQueueStatus(queueUebersicht())).catch(() => {}); };
    syncUndAnzeigen();
    queueSyncRef.current = setInterval(syncUndAnzeigen, 30000);
    return () => {
      clearInterval(iv);
      if (queueSyncRef.current) clearInterval(queueSyncRef.current);
    };
  }, [authenticated, loadTicker, state]);

  // ─── Return to idle ───
  const goIdle = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setState('idle');
    setScanData(null);
    setError(null);
    setPinMode(false);
    setPinDigits('');
    scanIdentRef.current = null;
    currentIdentRef.current = null;
    setNochWartenMin(0);
    setKannBeenden(false);
    setMittagElapsed(0);
    setCountdownMs(0);
    setGrundAktion(null);
    setGrundWarnung(null);
    setGrundMinuten(null);
    setGrundLimit(null);
    setGrundManuell('');
    setGrundManuellMode(false);
    setRegelBlock(null);
    // #1817 — Ein abgebrochener Offline-Scan darf seine Identitaet nicht
    // behalten: sonst puffert der naechste Timeout den Stempel des Vorgaengers.
    offlineIdentRef.current = null;
  }, []);

  // ─── Start timeout with countdown bar ───
  const startTimeout = useCallback((ms) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(goIdle, ms);
    setCountdownMs(ms);
    setCountdownKey(k => k + 1);
  }, [goIdle]);

  // ─── Scan response router ───
  const handleScanResponse = useCallback((data, ident) => {
    setScanData(data);
    scanIdentRef.current = ident;
    currentIdentRef.current = ident;
    switch (data.status) {
      case 'nicht_eingestempelt':
        setState('auswahl_ein');
        startTimeout(TIMEOUTS.auswahl);
        playSound('ein');
        break;
      case 'eingestempelt':
        setState('auswahl_aus');
        startTimeout(TIMEOUTS.auswahl);
        playSound('ein');
        break;
      case 'in_pause':
        setState('in_pause');
        startTimeout(TIMEOUTS.auswahl);
        playSound('ein');
        break;
      case 'in_mittagspause':
        setState('in_mittag');
        startTimeout(TIMEOUTS.mittag);
        setKannBeenden(data.kann_beenden || false);
        setNochWartenMin(data.noch_warten_min || 0);
        setMittagMindest(data.mindestdauer || 30);
        setMittagElapsed(data.pause_dauer_min || 0);
        playSound('ein');
        break;
      default:
        setError(data.error || t('kiosk.fehler', 'Fehler'));
        setState('fehler');
        playSound('fehler');
        startTimeout(TIMEOUTS.fehler);
    }
    loadTicker();
  }, [loadTicker, startTimeout, t]);

  // ─── Kiosk Voice TTS ───
  function playKioskVoice(mitarbeiterId, aktion) {
    if (!voiceEnabledRef.current || !mitarbeiterId) return;
    fetch(`${API}/api/kiosk/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getKioskAuth() },
      body: JSON.stringify({ mitarbeiter_id: mitarbeiterId, aktion }),
    })
      .then(r => { if (r.ok && r.status !== 204) return r.blob(); return null; })
      .then(blob => {
        if (!blob) return;
        const audio = new Audio(URL.createObjectURL(blob));
        audio.play().catch(() => {});
      })
      .catch(() => {}); // Voice is optional — never block stempeln
  }

  // ─── Action success router ───
  const handleAktionErfolg = useCallback((data) => {
    setScanData(data);
    if (data.aktion === 'arbeitsende') {
      setState('feierabend');
      startTimeout(TIMEOUTS.feierabend);
      playSound('aus');
    } else if (data.aktion === 'arbeit_start') {
      setState('bestaetigung_ein');
      startTimeout(TIMEOUTS.bestaetigung);
      playSound('ein');
    } else {
      // kurze_pause, mittagspause, pause_ende, mittag_ende
      setState('bestaetigung_pause');
      startTimeout(TIMEOUTS.bestaetigung);
      playSound('ein');
    }
    // Play TTS voice (fire & forget, never blocks)
    playKioskVoice(data.mitarbeiter_id || data.mitarbeiter?.id, data.aktion);
    loadTicker();
  }, [loadTicker, startTimeout]);

  // ─── 423 = Terminal gesperrt (#1816) ───
  // Der Server ist die Wahrheit über den Betriebszustand. Wenn er einen Scan mit
  // 423 abweist, war dieses Tablet noch nicht auf dem Stand (z. B. Sperre von
  // einem zweiten Fenster aus) — dann zeigen wir sofort den Sperrschirm statt
  // einer Fehlermeldung, die niemand einordnen kann.
  const istGesperrtAntwort = useCallback((res) => {
    if (res.status !== 423) return false;
    setGesperrt(true);
    setEntsperrPin('');
    setEntsperrError(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setState('idle');
    setScanData(null);
    setError(null);
    setPinMode(false);
    setPinDigits('');
    playSound('fehler');
    return true;
  }, []);

  // ─── Barcode scan ───
  const handleBarcodeScan = useCallback(async (barcode) => {
    if (!barcode || barcode.length < 4 || processingRef.current) return;
    processingRef.current = true;
    setProcessing(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/stempel/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getKioskAuth() },
        body: JSON.stringify({ barcode }),
      });
      const data = await res.json().catch(() => ({}));
      if (istGesperrtAntwort(res)) return;
      if (res.ok) {
        if (data.erfolg) handleAktionErfolg(data);
        else if (data.status) handleScanResponse(data, { barcode });
        else {
          setError(data.error || t('kiosk.fehler', 'Fehler'));
          setState('fehler');
          playSound('fehler');
          startTimeout(TIMEOUTS.fehler);
        }
      } else {
        // #1863 — Der Server begruendet die Ablehnung jetzt (`rule_meldung` +
        // `code`); frueher stand hier fuer jeden Fehlschlag „Mitarbeiter nicht
        // gefunden". `error` bleibt als Rueckfall, damit ein Tablet mit altem
        // Backend nicht ins Leere laeuft.
        setError(data.rule_meldung || data.error || `Fehler (${res.status})`);
        setState('fehler');
        playSound('fehler');
        startTimeout(TIMEOUTS.fehler);
      }
    } catch {
      // #1817 — Offline. Frueher wanderte hier eine Buchung OHNE Aktion in die
      // Queue; der Server las sie als Status-Abfrage, antwortete 200 und die
      // Arbeitszeit war weg. Die Aktion existiert an dieser Stelle schlicht
      // noch nicht: der Kiosk-Ablauf ist zweistufig (Scan -> der Server sagt,
      // was moeglich ist -> Auswahl). Ohne Server gibt es diese Auskunft nicht.
      //
      // Statt zu raten fragen wir den Mitarbeiter: er weiss, ob er kommt oder
      // geht. Erst diese Antwort wird gepuffert — dann ist sie buchbar.
      offlineIdentRef.current = { barcode };
      setState('offline_auswahl');
      playSound('fehler');
      startTimeout(TIMEOUTS.grund);
    } finally {
      processingRef.current = false;
      setProcessing(false);
    }
  }, [handleScanResponse, handleAktionErfolg, startTimeout, istGesperrtAntwort, t]);

  // ─── PIN submit ───
  const handlePinSubmit = useCallback(async (pin) => {
    if (!pin || pin.length < 4 || processing) return;
    setProcessing(true);
    setError(null);
    if (pinTimeoutRef.current) clearTimeout(pinTimeoutRef.current);
    try {
      const res = await fetch(`${API}/api/stempel/pin-scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getKioskAuth() },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json().catch(() => ({}));
      if (istGesperrtAntwort(res)) return;
      if (res.ok) {
        setPinMode(false);
        setPinDigits('');
        if (data.erfolg) handleAktionErfolg(data);
        else if (data.status) handleScanResponse(data, { pin });
        else {
          setPinShake(true);
          setTimeout(() => setPinShake(false), 500);
          setPinDigits('');
          playSound('fehler');
        }
      } else {
        setPinShake(true);
        setTimeout(() => setPinShake(false), 500);
        setPinDigits('');
        // #1863 — Der Status allein sagt nicht, WAS schiefging: hinter einem 404
        // steckte frueher auch „Konto stillgelegt" und „Sammelkonto", und das
        // Terminal schrieb in allen Faellen „PIN nicht gefunden". Der Server
        // liefert den Grund jetzt mit; der Status ist nur noch der Rueckfall.
        setError(data.rule_meldung || data.error
          || (res.status === 404 ? t('kiosk.pinUnbekannt', 'PIN nicht gefunden') : `Fehler (${res.status})`));
        playSound('fehler');
      }
      loadTicker();
    } catch {
      // #1817 Nachtrag — Der PIN-Weg bekommt BEWUSST keine Offline-Queue.
      // Die PIN ist hier nicht ein zweiter Faktor, sondern das Identitaets-
      // merkmal selbst: `pin-scan` findet den Mitarbeiter ueber
      // `WHERE stempel_pin = $1`. Ohne Server laesst sie sich nicht in eine
      // MA-ID aufloesen — puffern liesse sich also nur die PIN im Klartext,
      // und die gehoert auf einem geteilten Tablet nicht ins localStorage.
      // Niemand verliert dadurch den Offline-Weg: jeder aktive Mitarbeiter
      // hat einen Ausweis (39/39 auf LIVE), und der wird gepuffert.
      setPinShake(true);
      setTimeout(() => setPinShake(false), 500);
      setPinDigits('');
      setError(t('kiosk.offline_pin', 'Keine Verbindung — bitte den Ausweis scannen.'));
      playSound('fehler');
    } finally {
      setProcessing(false);
    }
  }, [processing, loadTicker, handleScanResponse, handleAktionErfolg, istGesperrtAntwort, t]);

  // ─── Action handler ───
  const handleAction = useCallback(async (aktion, pauseGrund = null, grundKategorie = null) => {
    // #817 — Use currentIdentRef (stable) instead of scanIdentRef (may be cleared
    // by HID-scanner keystroke capture during Sonstiges text input).
    const ident = currentIdentRef.current || scanIdentRef.current;
    if (!ident || processing) return;
    setProcessing(true);
    const usePin = !!ident.pin;
    const endpoint = usePin ? '/api/stempel/pin-scan' : '/api/stempel/scan';
    const bodyKey = usePin
      ? { pin: ident.pin }
      : { barcode: ident.barcode };
    try {
      const body = { ...bodyKey, aktion };
      if (pauseGrund) body.pause_grund = pauseGrund;
      if (grundKategorie) body.grund_kategorie = grundKategorie;
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getKioskAuth() },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (istGesperrtAntwort(res)) return;
      // Regel 2 — Backend fordert Grund ein
      if (res.ok && data.requires_reason) {
        setGrundAktion(aktion);
        setGrundWarnung(data.warning || null);
        setGrundMinuten(data.minuten_gearbeitet ?? null);
        setGrundLimit(data.minuten_limit ?? data.limit ?? null);
        setGrundManuell('');
        setGrundManuellMode(false);
        setState('grund_eingabe');
        startTimeout(TIMEOUTS.grund);
        playSound('ein');
        return;
      }
      // #817 — Regel-Block (MAX_ANZAHL, ABSTAND_ZU_KURZ, BEREITS_MITTAG, ...)
      if (res.ok && data.rule_blocked) {
        setRegelBlock({
          code: data.rule_code,
          meldung: data.rule_meldung,
          details: data.rule_details || [],
          aktion: data.aktion || aktion,
        });
        setState('regel_block');
        startTimeout(TIMEOUTS.fehler);
        playSound('fehler');
        return;
      }
      if (res.ok && data.erfolg) handleAktionErfolg(data);
      else {
        setError(data.error || t('kiosk.fehler', 'Fehler'));
        setState('fehler');
        playSound('fehler');
        startTimeout(TIMEOUTS.fehler);
      }
    } catch {
      // #1817 — Hier ist die Aktion bekannt und vom Mitarbeiter bestaetigt,
      // also ist sie auch puffbar. Vorher brach dieser Pfad ersatzlos ab: die
      // Auswahl war getroffen, das Netz weg, und der Stempel schlicht weg.
      const gepuffert = queueStempel(ident, aktion, {
        ...(pauseGrund ? { pause_grund: pauseGrund } : {}),
        ...(grundKategorie ? { grund_kategorie: grundKategorie } : {}),
      });
      setQueueStatus(queueUebersicht());
      setError(gepuffert
        ? t('kiosk.offline_gepuffert', 'Keine Verbindung — Stempel gemerkt und wird automatisch nachgeholt.')
        : t('kiosk.offline', 'Keine Verbindung — nicht gespeichert. Bitte erneut versuchen.'));
      setState('fehler');
      playSound('fehler');
      startTimeout(TIMEOUTS.fehler);
    } finally {
      setProcessing(false);
    }
  }, [processing, handleAktionErfolg, startTimeout, istGesperrtAntwort, t]);

  // #1817 — Offline-Auswahl bestaetigen: jetzt liegt eine echte Aktion vor.
  const offlineBuchen = useCallback((aktion) => {
    const ident = offlineIdentRef.current;
    if (!ident) return;
    offlineIdentRef.current = null;
    const gepuffert = queueStempel(ident, aktion);
    setQueueStatus(queueUebersicht());
    setError(gepuffert
      ? t('kiosk.offline_gepuffert', 'Keine Verbindung — Stempel gemerkt und wird automatisch nachgeholt.')
      : t('kiosk.offline', 'Keine Verbindung — nicht gespeichert. Bitte erneut versuchen.'));
    setState('fehler');
    startTimeout(TIMEOUTS.fehler);
  }, [startTimeout, t]);

  // ─── Submit Grund-Eingabe (Regel 2) ───
  const submitGrund = useCallback((grund, kategorie) => {
    if (!grund || !grundAktion) return;
    const ak = grundAktion;
    setGrundAktion(null);
    setGrundWarnung(null);
    setGrundMinuten(null);
    setGrundLimit(null);
    setGrundManuell('');
    setGrundManuellMode(false);
    handleAction(ak, grund, kategorie || 'sonstiges');
  }, [grundAktion, handleAction]);

  // ─── Abbrechen auf Grund-Screen: zurück zum Auswahl-Screen (nicht IDLE) ───
  const cancelGrund = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setGrundAktion(null);
    setGrundWarnung(null);
    setGrundMinuten(null);
    setGrundLimit(null);
    setGrundManuell('');
    setGrundManuellMode(false);
    setState('auswahl_aus');
    startTimeout(TIMEOUTS.auswahl);
  }, [startTimeout]);

  // ─── Mittagspause timer ───
  useEffect(() => {
    if (state !== 'in_mittag' || kannBeenden) return;
    const iv = setInterval(() => {
      setMittagElapsed(prev => prev + 1);
      setNochWartenMin(prev => {
        const next = prev - 1;
        if (next <= 0) { setKannBeenden(true); return 0; }
        return next;
      });
    }, 60000);
    return () => clearInterval(iv);
  }, [state, kannBeenden]);

  // ─── PIN timeout (30s inactivity) ───
  const resetPinTimeout = useCallback(() => {
    if (pinTimeoutRef.current) clearTimeout(pinTimeoutRef.current);
    pinTimeoutRef.current = setTimeout(() => {
      setPinDigits('');
      setPinMode(false);
    }, 30000);
  }, []);

  // ─── Global keydown handler for HID barcode scanner ───
  // HID scanners send rapid keystrokes (each within ~30ms) followed by Enter.
  // This collects characters from rapid keystrokes and fires handleBarcodeScan on Enter.
  // Active on ALL screens — if user is on action/confirmation screen, scanner re-triggers flow.
  // IMPORTANT: Registered ONCE (empty deps). Uses refs to read current state/functions
  // so the handler is never torn down during scanner input.
  useEffect(() => {
    const MAX_KEY_GAP = 150; // ms — gap after last keystroke before processing buffer
    const MIN_BARCODE_LEN = 4;

    // Processes a collected barcode string — resets state if needed, then calls scan handler
    const processBuffer = () => {
      const barcode = scanBufferRef.current.trim();
      scanBufferRef.current = '';
      if (barcode.length >= MIN_BARCODE_LEN) {
        // If not idle, go idle first then scan
        if (stateRef.current !== 'idle') {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          setState('idle');
          setScanData(null);
          setError(null);
          setPinMode(false);
          setPinDigits('');
          scanIdentRef.current = null;
          setNochWartenMin(0);
          setKannBeenden(false);
          setMittagElapsed(0);
          setCountdownMs(0);
        }
        handleBarcodeScanRef.current(barcode);
      }
    };

    const handler = (e) => {
      // Don't capture when PIN numpad is active or admin PIN entry
      if (pinModeRef.current || !authenticatedRef.current) return;
      // #1816 — Ein gesperrtes Terminal nimmt keine Scans an. Der Ausweis-Scanner
      // ist eine Tastatur: ohne diesen Riegel liefe der Scan hinter dem
      // Sperrschirm weiter und der Server müsste ihn abweisen.
      if (gesperrtRef.current) return;
      // #1824 — Gleiches gilt für den Notausgang: er liegt über dem Scan-Schirm,
      // `authenticated` ist dort noch true. Ohne diesen Riegel würde ein Scan
      // während der PIN-Eingabe unsichtbar im Hintergrund gebucht.
      if (notausgangRef.current) return;
      // #817 — Don't steal keystrokes from focused input/textarea (Sonstiges-Textfeld).
      // Sonst baut der Scanner-Buffer den getippten Text als "Barcode" zusammen und
      // löst eine Neu-Identifikation aus, die scanIdentRef cleart.
      const tgt = e.target;
      if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.isContentEditable)) return;

      // Enter = explicit scan-end (some scanners send CR suffix)
      if (e.key === 'Enter') {
        if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
        if (scanBufferRef.current.trim().length >= MIN_BARCODE_LEN) {
          e.preventDefault();
          processBuffer();
        }
        return;
      }

      // Only single printable characters (scanner HID output)
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
        scanBufferRef.current += e.key;
        // After gap with no more keys: process as barcode (handles scanners without Enter suffix)
        scanTimerRef.current = setTimeout(processBuffer, MAX_KEY_GAP);
      }
    };

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    };
  }, []); // eslint-disable-line — intentionally empty: refs keep handler current without re-registration

  // Debug overlay removed (#637 — scanner fix confirmed working)

  // ─── PIN digit handler ───
  const onPinDigit = useCallback((d) => {
    if (processing || pinDigits.length >= 4) return;
    const next = pinDigits + d;
    setPinDigits(next);
    setError(null);
    resetPinTimeout();
    if (next.length === 4) handlePinSubmit(next);
  }, [processing, pinDigits, resetPinTimeout, handlePinSubmit]);

  const onPinDelete = useCallback(() => {
    setPinDigits(prev => prev.slice(0, -1));
    setError(null);
    resetPinTimeout();
  }, [resetPinTimeout]);

  const exitPin = useCallback(() => {
    setPinMode(false);
    setPinDigits('');
    setError(null);
    if (pinTimeoutRef.current) clearTimeout(pinTimeoutRef.current);
  }, []);

  // ─── Derived ───
  const vorname = scanData?.mitarbeiter?.name?.split(' ')[0] || '';

  // ─── Admin PIN submit handler ───
  const handleAdminPinSubmit = useCallback(async (pin) => {
    if (!pin || pin.length < 4 || adminPinLoading) return;
    setAdminPinLoading(true);
    setAdminPinError(null);
    try {
      const res = await fetch(`${API}/api/kiosk/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.token) {
        localStorage.setItem('kiosk_token', data.token);
        setAuthenticated(true);
        setAdminPin('');
      } else {
        setAdminPinShake(true);
        setTimeout(() => setAdminPinShake(false), 500);
        setAdminPin('');
        setAdminPinError(data.error || t('kiosk.adminPinFalsch', 'Falsche PIN'));
      }
    } catch {
      setAdminPinError(t('kiosk.offline', 'Offline'));
    } finally {
      setAdminPinLoading(false);
    }
  }, [adminPinLoading, t]);

  // #1816 — KEIN Auto-Absenden mehr bei vier Ziffern: die PIN darf 4–6 Ziffern
  // haben, und die generierte hat sechs. Ein Auto-Submit nach der vierten
  // Ziffer leerte das Feld und machte jede längere PIN unbenutzbar.
  const onAdminPinDigit = useCallback((d) => {
    if (adminPinLoading || adminPin.length >= 6) return;
    setAdminPin(adminPin + d);
    setAdminPinError(null);
  }, [adminPinLoading, adminPin]);

  const onAdminPinDelete = useCallback(() => {
    setAdminPin(prev => prev.slice(0, -1));
    setAdminPinError(null);
  }, []);

  // ─── Kopplungscode am Terminal einlösen (#1820) ───
  // KEIN `window.location` und kein Router-Wechsel: nach der Einlösung wird
  // derselbe Seitenzustand weitergeschaltet. Die Kiosk-App bleibt damit auf
  // ihrer fest eingestellten Adresse stehen; alle Effekte dieser Seite hängen
  // an `authenticated` und laufen beim Umschalten von allein an.
  const handleKoppeln = useCallback(async (code) => {
    if (code.length !== 8 || koppelLoading) return;
    setKoppelLoading(true);
    setKoppelError(null);
    const ergebnis = await koppleGeraet(code);
    if (!ergebnis.ok) {
      setKoppelShake(true);
      setTimeout(() => setKoppelShake(false), 500);
      setKoppelCode('');
      setKoppelError(ergebnis.error);
      setKoppelLoading(false);
      return;
    }
    // Registrierung beim Server bestätigen lassen, statt sie aus der
    // Antwort anzunehmen: `geraet`/`gesperrt` sind seit #1816 Server-Wahrheit,
    // und ein frisch gekoppeltes Gerät ist entsperrt (der Server setzt
    // `gesperrt_seit` bei der Kopplung zurück).
    await ladeGeraetStatus();
    setKoppelCode('');
    setKoppelLoading(false);
    setAuthenticated(true);
  }, [koppelLoading, ladeGeraetStatus]);

  const onKoppelDigit = useCallback((d) => {
    if (koppelLoading || koppelCode.length >= 8) return;
    setKoppelCode(prev => prev + d);
    setKoppelError(null);
  }, [koppelLoading, koppelCode]);

  // ─── Notausgang: langer Druck aufs Logo (#1824 Teil B) ───
  const startLogoDruck = useCallback(() => {
    if (logoDruckRef.current) clearTimeout(logoDruckRef.current);
    logoDruckRef.current = setTimeout(() => {
      logoDruckRef.current = null;
      setNotausgangPin('');
      setNotausgangError(null);
      setNotausgang('pin');
    }, LOGO_DRUCK_MS);
  }, []);

  // Losgelassen, weggewischt oder vom System abgebrochen: alles beendet den
  // Druck. Ohne `onPointerCancel` bliebe der Timer stehen, wenn das Tablet die
  // Geste als Scrollen übernimmt — der Schirm ginge dann verzögert von selbst auf.
  const endeLogoDruck = useCallback(() => {
    if (logoDruckRef.current) {
      clearTimeout(logoDruckRef.current);
      logoDruckRef.current = null;
    }
  }, []);

  useEffect(() => endeLogoDruck, [endeLogoDruck]);

  const handleNotausgangPin = useCallback(async (pin) => {
    if (pin.length < 4 || notausgangLoading) return;
    setNotausgangLoading(true);
    setNotausgangError(null);
    try {
      // Prüft NUR die PIN. Bewusst nicht `/api/kiosk/auth` — das stellte ein
      // frisches 30-Tage-Token aus und legte damit genau den Zustand wieder an,
      // aus dem der Notausgang herausführen soll.
      const res = await fetch(`${API}/api/kiosk-geraete/pin-pruefen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setNotausgangPin('');
        setNotausgang('bestaetigen');
      } else {
        setNotausgangShake(true);
        setTimeout(() => setNotausgangShake(false), 500);
        setNotausgangPin('');
        setNotausgangError(data.error || t('kiosk.adminPinFalsch', 'Falsche PIN'));
      }
    } catch {
      setNotausgangError(t('kiosk.offline', 'Offline'));
    } finally {
      setNotausgangLoading(false);
    }
  }, [notausgangLoading, t]);

  const onNotausgangDigit = useCallback((d) => {
    if (notausgangLoading || notausgangPin.length >= 6) return;
    setNotausgangPin(prev => prev + d);
    setNotausgangError(null);
  }, [notausgangLoading, notausgangPin]);

  /**
   * Alle DREI lokalen Ausweise fallen zusammen, nicht nur der Gerätetoken:
   * ein zurückgebliebener `kiosk_token` oder `stempel_api_key` hielte die Seite
   * weiter für angemeldet (`authenticated` startet aus genau diesen Schlüsseln)
   * — das Tablet stünde nach dem Neu-Koppeln wieder auf der Scan-Seite.
   * Dieselbe Reihenfolge wie beim Widerruf in `ladeGeraetStatus()`.
   *
   * Rein lokal: der Server behält die Registrierung. Wer sie auch dort beenden
   * will, widerruft das Terminal in der Geräteliste — das ist eine andere
   * Entscheidung als „dieses Tablet neu einrichten".
   */
  const handleNeuKoppeln = useCallback(() => {
    clearGeraetToken();
    try {
      localStorage.removeItem('kiosk_token');
      localStorage.removeItem('stempel_api_key');
    } catch { /* Private-Mode: dann lag dort ohnehin nichts */ }
    setGeraet(null);
    setGesperrt(false);
    setSetupModus('code');
    setNotausgang(null);
    setNotausgangPin('');
    setNotausgangError(null);
    setAuthenticated(false);
    setAuthChecked(true);
  }, []);

  // ─── Betriebsschalter: sperren / entsperren (#1816) ───
  // Sperren braucht keine PIN — wer am Tablet steht, darf es außer Betrieb
  // nehmen. Der Schlüssel sitzt am Rückweg.
  const handleSperren = useCallback(async () => {
    if (!geraet) return;
    try {
      const res = await fetch(`${API}/api/kiosk-geraete/sperren`, {
        method: 'POST', headers: geraetHeaders(),
      });
      if (res.ok) {
        setGesperrt(true);
        setEntsperrModus(false);
        setEntsperrPin('');
        setEntsperrError(null);
        // Laufende Anzeige abräumen, damit hinter dem Sperrschirm kein
        // halbfertiger Scan-Dialog stehen bleibt.
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setState('idle');
        setScanData(null);
        setError(null);
        setPinMode(false);
        setPinDigits('');
      }
    } catch { /* offline — Sperre ist serverseitig, ohne Server keine Sperre */ }
  }, [geraet]);

  const handleEntsperren = useCallback(async (pin) => {
    if (!pin || pin.length < 4 || entsperrLoading) return;
    setEntsperrLoading(true);
    setEntsperrError(null);
    try {
      const res = await fetch(`${API}/api/kiosk-geraete/entsperren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...geraetHeaders() },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.gesperrt === false) {
        setGesperrt(false);
        setEntsperrModus(false);
        setEntsperrPin('');
      } else {
        setEntsperrShake(true);
        setTimeout(() => setEntsperrShake(false), 500);
        setEntsperrPin('');
        setEntsperrError(data.error || t('kiosk.adminPinFalsch', 'Falsche PIN'));
      }
    } catch {
      setEntsperrError(t('kiosk.offline', 'Offline'));
    } finally {
      setEntsperrLoading(false);
    }
  }, [entsperrLoading, t]);

  const onEntsperrDigit = useCallback((d) => {
    if (entsperrLoading || entsperrPin.length >= 6) return;
    setEntsperrPin(prev => prev + d);
    setEntsperrError(null);
  }, [entsperrLoading, entsperrPin]);

  // ════════════════════════════════════════════════════════════════
  //  GESPERRT → Terminal deaktiviert (#1816)
  // ════════════════════════════════════════════════════════════════
  // Der Zustand kommt vom Server: Neuladen hebt ihn nicht auf, und eine
  // Fernentsperrung aus der Terminal-Liste räumt diesen Schirm von allein weg.
  if (gesperrt) {
    return (
      <div style={S.root}>
        <div style={S.screenWrap} className="kiosk-fade-in">
          <div style={S.pinWrap}>
            <img src="/weship-logo.png" alt="WeShip" style={{ height: 36, marginBottom: 24, opacity: 0.4 }} />
            <h2 style={S.pinTitle}>{t('kiosk.gesperrtTitel', 'Terminal gesperrt')}</h2>
            <p style={S.pinHint}>
              {geraet?.name ? `${geraet.name} — ` : ''}
              {t('kiosk.gesperrtHint', 'Dieses Terminal nimmt keine Scans an.')}
            </p>

            {!entsperrModus ? (
              <button
                onClick={() => { setEntsperrModus(true); setEntsperrPin(''); setEntsperrError(null); }}
                style={S.entsperrBtn}
              >
                {t('kiosk.entsperren', 'Entsperren')}
              </button>
            ) : (
              <>
                <p style={S.pinHint}>{t('kiosk.entsperrenHint', 'Terminal-PIN eingeben')}</p>

                <div className={entsperrShake ? 'kiosk-shake' : ''} style={S.pinDots}>
                  {[0, 1, 2, 3, 4, 5].map(i => (
                    <div
                      key={i}
                      style={{
                        ...S.pinDot,
                        background: i < entsperrPin.length ? '#338edc' : '#D1D5DB',
                        transform: i < entsperrPin.length ? 'scale(1.15)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>

                {entsperrError && <p style={S.pinError}>{entsperrError}</p>}

                <div style={S.numpad}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
                    <button key={d} onClick={() => onEntsperrDigit(String(d))} style={S.numBtn}>
                      {d}
                    </button>
                  ))}
                  <button
                    onClick={() => { setEntsperrPin(prev => prev.slice(0, -1)); setEntsperrError(null); }}
                    disabled={!entsperrPin.length}
                    style={{ ...S.numBtn, opacity: entsperrPin.length ? 1 : 0.3 }}
                  >
                    &#9003;
                  </button>
                  <button onClick={() => onEntsperrDigit('0')} style={S.numBtn}>0</button>
                  <button
                    onClick={() => entsperrPin.length >= 4 && handleEntsperren(entsperrPin)}
                    disabled={entsperrPin.length < 4}
                    style={{ ...S.numBtn, fontSize: 'clamp(0.8rem, 1.3vw, 1rem)', opacity: entsperrPin.length >= 4 ? 1 : 0.3 }}
                  >
                    OK
                  </button>
                </div>

                {entsperrLoading && (
                  <p style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)', color: '#338edc', fontWeight: 500, marginTop: 12 }}>
                    {t('kiosk.pruefePIN', 'Prüfe PIN...')}
                  </p>
                )}

                <button
                  onClick={() => { setEntsperrModus(false); setEntsperrPin(''); setEntsperrError(null); }}
                  style={S.entsperrZurueck}
                >
                  {t('kiosk.entsperrenAbbrechen', 'Abbrechen')}
                </button>
              </>
            )}
          </div>
        </div>

        <style>{`
          @keyframes kioskFadeIn {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .kiosk-fade-in { animation: kioskFadeIn 0.3s ease-out; }
          @keyframes kioskShake {
            0%,100% { transform: translateX(0); }
            20%,60% { transform: translateX(-8px); }
            40%,80% { transform: translateX(8px); }
          }
          .kiosk-shake { animation: kioskShake 0.5s ease-in-out; }
          * { -webkit-tap-highlight-color: transparent; }
        `}</style>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  //  NICHT EINGERICHTET → Kopplungscode (Regelweg) oder PIN (Notweg), #1820
  // ════════════════════════════════════════════════════════════════
  // AK5 — Ein registriertes Gerät darf diesen Schirm nie sehen. Ohne
  // `authChecked` stünde er in der Zeit, die die Statusabfrage braucht, auch
  // vor einem längst gekoppelten Tablet: `authenticated` startet aus dem
  // localStorage und weiß von der Registrierung noch nichts.
  if (!authenticated && !authChecked) {
    return (
      <div style={S.root}>
        <div style={S.screenWrap}>
          <img src="/weship-logo.png" alt="WeShip" style={{ height: 36, opacity: 0.4 }} />
        </div>
      </div>
    );
  }

  if (!authenticated) {
    const codeModus = setupModus === 'code';
    // Beide Wege teilen sich EIN Nummernfeld. Zwei getrennte Numpads wären
    // zwei Stellen, an denen dieselbe Tastatur gepflegt werden müsste.
    const wert = codeModus ? koppelCode : adminPin;
    const minLen = 4;
    const laden = codeModus ? koppelLoading : adminPinLoading;
    const fehler = codeModus ? koppelError : adminPinError;
    const schuettelt = codeModus ? koppelShake : adminPinShake;
    const onDigit = codeModus ? onKoppelDigit : onAdminPinDigit;
    const onDelete = codeModus
      ? () => { setKoppelCode(prev => prev.slice(0, -1)); setKoppelError(null); }
      : onAdminPinDelete;
    const onOk = codeModus
      ? () => handleKoppeln(koppelCode)
      : () => adminPin.length >= minLen && handleAdminPinSubmit(adminPin);
    const okAktiv = codeModus ? koppelCode.length === 8 : adminPin.length >= minLen;

    return (
      <div style={S.root}>
        <div style={S.screenWrap} className="kiosk-fade-in">
          <div style={S.pinWrap}>
            <img src="/weship-logo.png" alt="WeShip" style={{ height: 36, marginBottom: 24 }} />
            <h2 style={S.pinTitle}>{t('kiosk.adminPinTitel', 'Terminal einrichten')}</h2>
            <p style={S.pinHint}>
              {codeModus
                ? t('kiosk.setupCodeHint', 'Kopplungscode aus den Einstellungen eingeben')
                : t('kiosk.adminPinHint', 'Terminal-PIN eingeben')}
            </p>

            {codeModus ? (
              // Der Kopplungscode wird angezeigt statt verdeckt: er ist kein
              // Geheimnis, sondern ein einmaliger, kurzlebiger Schlüssel — und
              // acht verdeckte Punkte lassen sich am Tablet nicht kontrollieren.
              <div
                className={schuettelt ? 'kiosk-shake' : ''}
                style={{
                  fontSize: 'clamp(2rem, 4vw, 3rem)',
                  fontFamily: "'DM Mono', ui-monospace, monospace",
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  color: koppelCode ? '#1A1A2E' : '#D1D5DB',
                  marginBottom: 'clamp(16px, 2vh, 28px)',
                }}
              >
                {(koppelCode + '••••••••').slice(0, 8)}
              </div>
            ) : (
              <div className={schuettelt ? 'kiosk-shake' : ''} style={S.pinDots}>
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <div
                    key={i}
                    style={{
                      ...S.pinDot,
                      background: i < adminPin.length ? '#338edc' : '#D1D5DB',
                      transform: i < adminPin.length ? 'scale(1.15)' : 'scale(1)',
                    }}
                  />
                ))}
              </div>
            )}

            {fehler && <p style={S.pinError}>{fehler}</p>}

            {/* Numpad */}
            <div style={S.numpad}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
                <button key={d} onClick={() => onDigit(String(d))} style={S.numBtn}>
                  {d}
                </button>
              ))}
              <button
                onClick={onDelete}
                disabled={!wert.length}
                style={{ ...S.numBtn, opacity: wert.length ? 1 : 0.3 }}
              >
                &#9003;
              </button>
              <button onClick={() => onDigit('0')} style={S.numBtn}>0</button>
              <button
                onClick={onOk}
                disabled={!okAktiv || laden}
                style={{ ...S.numBtn, fontSize: 'clamp(0.8rem, 1.3vw, 1rem)', opacity: okAktiv ? 1 : 0.3 }}
              >
                OK
              </button>
            </div>

            {laden && (
              <p style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)', color: '#338edc', fontWeight: 500, marginTop: 12 }}>
                {codeModus ? t('kiosk.koppelt', 'Gerät wird gekoppelt…') : t('kiosk.pruefePIN', 'Prüfe PIN...')}
              </p>
            )}

            {/* Der jeweils andere Weg — bewusst unauffällig, damit am Tablet
                der Kopplungscode der offensichtliche Weg bleibt. Die Eingabe
                wird beim Wechsel geleert: acht Code-Ziffern sind in einer
                6-stelligen PIN sonst eine stille Fehleingabe. */}
            <button
              onClick={() => {
                setSetupModus(codeModus ? 'pin' : 'code');
                setKoppelCode(''); setKoppelError(null);
                setAdminPin(''); setAdminPinError(null);
              }}
              style={{
                marginTop: 'clamp(16px, 2.5vh, 28px)',
                background: 'none',
                border: 'none',
                color: '#6B7280',
                fontSize: 'clamp(0.8rem, 1.1vw, 0.95rem)',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {codeModus
                ? t('kiosk.setupZurPin', 'Kein Kopplungscode? Mit Terminal-PIN fortfahren')
                : t('kiosk.setupZumCode', 'Zurück zum Kopplungscode')}
            </button>
          </div>
        </div>

        <style>{`
          @keyframes kioskFadeIn {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .kiosk-fade-in { animation: kioskFadeIn 0.3s ease-out; }
          @keyframes kioskShake {
            0%,100% { transform: translateX(0); }
            20%,60% { transform: translateX(-8px); }
            40%,80% { transform: translateX(8px); }
          }
          .kiosk-shake { animation: kioskShake 0.5s ease-in-out; }
          * { -webkit-tap-highlight-color: transparent; }
        `}</style>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  //  NOTAUSGANG → Gerät neu koppeln (#1824 Teil B)
  // ════════════════════════════════════════════════════════════════
  // Erreichbar nur vom Scan-Schirm (langer Druck aufs Logo) und nur mit der
  // Terminal-PIN. Abbrechen führt ohne Nebenwirkung zurück — bis zur
  // ausdrücklichen Bestätigung ist noch nichts gelöscht.
  if (notausgang) {
    const bestaetigen = notausgang === 'bestaetigen';
    return (
      <div style={S.root}>
        <div style={S.screenWrap} className="kiosk-fade-in">
          <div style={S.pinWrap}>
            <img src="/weship-logo.png" alt="WeShip" style={{ height: 36, marginBottom: 24, opacity: 0.4 }} />
            <h2 style={S.pinTitle}>{t('kiosk.neuKoppelnTitel', 'Gerät neu koppeln')}</h2>

            {!bestaetigen ? (
              <>
                <p style={S.pinHint}>{t('kiosk.entsperrenHint', 'Terminal-PIN eingeben')}</p>

                <div className={notausgangShake ? 'kiosk-shake' : ''} style={S.pinDots}>
                  {[0, 1, 2, 3, 4, 5].map(i => (
                    <div
                      key={i}
                      style={{
                        ...S.pinDot,
                        background: i < notausgangPin.length ? '#338edc' : '#D1D5DB',
                        transform: i < notausgangPin.length ? 'scale(1.15)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>

                {notausgangError && <p style={S.pinError}>{notausgangError}</p>}

                <div style={S.numpad}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
                    <button key={d} onClick={() => onNotausgangDigit(String(d))} style={S.numBtn}>
                      {d}
                    </button>
                  ))}
                  <button
                    onClick={() => { setNotausgangPin(prev => prev.slice(0, -1)); setNotausgangError(null); }}
                    disabled={!notausgangPin.length}
                    style={{ ...S.numBtn, opacity: notausgangPin.length ? 1 : 0.3 }}
                  >
                    &#9003;
                  </button>
                  <button onClick={() => onNotausgangDigit('0')} style={S.numBtn}>0</button>
                  <button
                    onClick={() => notausgangPin.length >= 4 && handleNotausgangPin(notausgangPin)}
                    disabled={notausgangPin.length < 4}
                    style={{ ...S.numBtn, fontSize: 'clamp(0.8rem, 1.3vw, 1rem)', opacity: notausgangPin.length >= 4 ? 1 : 0.3 }}
                  >
                    OK
                  </button>
                </div>

                {notausgangLoading && (
                  <p style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)', color: '#338edc', fontWeight: 500, marginTop: 12 }}>
                    {t('kiosk.pruefePIN', 'Prüfe PIN...')}
                  </p>
                )}
              </>
            ) : (
              <>
                <p style={S.pinHint}>
                  {t('kiosk.neuKoppelnHint', 'Dieses Tablet wird abgemeldet und zeigt danach den Kopplungs-Schirm. Gebuchte Stempel bleiben erhalten.')}
                </p>
                <button onClick={handleNeuKoppeln} style={S.entsperrBtn}>
                  {t('kiosk.neuKoppelnBestaetigen', 'Dieses Gerät neu koppeln')}
                </button>
              </>
            )}

            <button
              onClick={() => { setNotausgang(null); setNotausgangPin(''); setNotausgangError(null); }}
              style={S.entsperrZurueck}
            >
              {t('kiosk.entsperrenAbbrechen', 'Abbrechen')}
            </button>
          </div>
        </div>

        <style>{`
          @keyframes kioskFadeIn {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .kiosk-fade-in { animation: kioskFadeIn 0.3s ease-out; }
          @keyframes kioskShake {
            0%,100% { transform: translateX(0); }
            20%,60% { transform: translateX(-8px); }
            40%,80% { transform: translateX(8px); }
          }
          .kiosk-shake { animation: kioskShake 0.5s ease-in-out; }
          * { -webkit-tap-highlight-color: transparent; }
        `}</style>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════════════
  return (
    <div style={S.root}>
      {/* Barcode scanner handled by global keydown listener — no hidden input needed */}

      {/* Debug overlay removed (#637) */}

      {/* ═══════════════════════════════════════════════ */}
      {/*  IDLE SCREEN                                   */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'idle' && !pinMode && (
        <div style={S.screenWrap} className="kiosk-fade-in">
          {/* Logo — zugleich der unauffällige Griff zum Notausgang (#1824 Teil B) */}
          <div
            style={{ ...S.logo, touchAction: 'manipulation' }}
            onPointerDown={startLogoDruck}
            onPointerUp={endeLogoDruck}
            onPointerLeave={endeLogoDruck}
            onPointerCancel={endeLogoDruck}
            onContextMenu={(e) => e.preventDefault()}
          >
            <img
              src="/weship-logo.png"
              alt="WeShip"
              draggable={false}
              style={{ height: 'clamp(28px, 4vh, 40px)', userSelect: 'none', WebkitUserDrag: 'none' }}
            />
          </div>

          {/* Clock — the hero */}
          <div style={S.clockWrap}>
            <div style={S.clockTime}>
              <span>{clockHM}</span>
              <span style={S.clockSeconds}>{clockS}</span>
            </div>
            <div style={S.clockDate}>{dateStr}</div>
          </div>

          {/* Scan prompt */}
          <div style={S.scanPrompt}>
            <div style={S.scanIconRing} className="kiosk-pulse">
              <svg width="clamp(32px, 4vw, 48px)" height="clamp(32px, 4vw, 48px)" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <path d="M14 14h3v3M17 17h3v3M14 20h3M20 14v3" />
              </svg>
            </div>
            <p style={S.scanText}>{t('kiosk.scannen', 'Bitte Ausweis scannen')}</p>
            <p style={S.scanSub}>{t('kiosk.scanHint', 'Halte deinen Ausweis vor den Scanner')}</p>
          </div>

          {/* Anwesend count */}
          <div style={S.anwesendBadge}>
            <span style={S.anwesendNumber}>{anwesendCount.anwesend}</span>
            <span style={S.anwesendLabel}>
              {t('kiosk.vonGesamt', { gesamt: anwesendCount.gesamt })}
            </span>
          </div>

          {/* Bottom bar */}
          <div style={S.bottomBar}>
            <button
              onClick={() => { setPinMode(true); setPinDigits(''); setError(null); resetPinTimeout(); }}
              style={S.bottomBtn}
            >
              {t('kiosk.pinEingeben', 'PIN eingeben')}
            </button>
            <button onClick={toggleFullscreen} style={S.bottomBtn}>
              {t('kiosk.vollbild', 'Vollbild')}
            </button>
            {/* #1816 — nur für registrierte Terminals: ein Ersatztablet hat
                keinen Eintrag in der Registry und damit nichts zu sperren.
                Bewusst unauffällig — der Schalter ist für den Umbau gedacht,
                nicht für den Alltag. */}
            {geraet && (
              <button onClick={handleSperren} style={S.bottomBtn}>
                {t('kiosk.sperren', 'Terminal sperren')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  PIN SCREEN                                    */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'idle' && pinMode && (
        <div style={S.screenWrap} className="kiosk-fade-in">
          <div style={S.pinWrap}>
            <h2 style={S.pinTitle}>{t('kiosk.pinTitel', 'PIN eingeben')}</h2>
            <p style={S.pinHint}>{t('kiosk.pinHint', '4-stellige PIN vom Ausweis')}</p>

            {/* Dots */}
            <div className={pinShake ? 'kiosk-shake' : ''} style={S.pinDots}>
              {[0, 1, 2, 3].map(i => (
                <div
                  key={i}
                  style={{
                    ...S.pinDot,
                    background: i < pinDigits.length ? '#338edc' : '#D1D5DB',
                    transform: i < pinDigits.length ? 'scale(1.15)' : 'scale(1)',
                  }}
                />
              ))}
            </div>

            {error && <p style={S.pinError}>{error}</p>}

            {/* Numpad */}
            <div style={S.numpad}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
                <button key={d} onClick={() => onPinDigit(String(d))} style={S.numBtn}>
                  {d}
                </button>
              ))}
              <button
                onClick={onPinDelete}
                disabled={!pinDigits.length}
                style={{ ...S.numBtn, opacity: pinDigits.length ? 1 : 0.3 }}
              >
                &#9003;
              </button>
              <button onClick={() => onPinDigit('0')} style={S.numBtn}>0</button>
              <button onClick={exitPin} style={{ ...S.numBtn, fontSize: 'clamp(0.8rem, 1.3vw, 1rem)' }}>
                {t('kiosk.abbruch', 'Abbruch')}
              </button>
            </div>

            {processing && (
              <p style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)', color: '#338edc', fontWeight: 500, marginTop: 12 }}>
                {t('kiosk.pruefePin', 'Prüfe PIN...')}
              </p>
            )}

            <button onClick={exitPin} style={S.cancelLink}>
              {t('kiosk.zurueckZumScanner', 'Zurück zum Scanner')}
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  AUSWAHL_EIN — nicht eingestempelt             */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'auswahl_ein' && scanData && (
        <div style={S.screenWrap} className="kiosk-fade-in" onClick={e => e.target === e.currentTarget && goIdle()}>
          <div style={S.erkannt}>
            <Avatar ma={scanData.mitarbeiter} api={API} />
            <h2 style={S.erkanntName}>{scanData.mitarbeiter?.name}</h2>
            <p style={S.erkanntGreeting}>
              {t('kiosk.gutenMorgen', { name: vorname })}
            </p>

            <BigButton
              color={COLORS.green}
              icon="check"
              label={t('kiosk.arbeitsbeginn')}
              onClick={() => handleAction('arbeit_start')}
              disabled={processing}
            />

            <button onClick={goIdle} style={S.cancelLink}>
              {t('kiosk.abbruch', 'Abbrechen')}
            </button>
          </div>
          <CountdownBar key={countdownKey} duration={countdownMs} />
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  AUSWAHL_AUS — eingestempelt (Smart-Modus)     */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'auswahl_aus' && scanData && (() => {
        const az = scanData.arbeitszeit_min || 0;
        const suggestFeierabend = az >= 240; // >4h → Feierabend als Hauptaktion
        // erlaubt_mit_grund: klickbar auch bei ZU_FRUEH (Grund-Flow); nur harte Blocker (MAX_ANZAHL,
        // BEREITS_MITTAG, ABSTAND_ZU_KURZ, NICHT_EINGESTEMPELT) disablen den Button.
        const kurzePauseOk = scanData.optionen?.kurze_pause?.erlaubt_mit_grund !== false;
        const mittagOk = scanData.optionen?.mittagspause?.erlaubt_mit_grund !== false;

        return (
          <div style={S.screenWrap} className="kiosk-fade-in" onClick={e => e.target === e.currentTarget && goIdle()}>
            <div style={S.erkannt}>
              <Avatar ma={scanData.mitarbeiter} api={API} />
              <h2 style={S.erkanntName}>{scanData.mitarbeiter?.name}</h2>
              <p style={S.erkanntSub}>
                {t('kiosk.eingestempeltSeit', { zeit: formatUhrzeit(scanData.seit) })}
                {' — '}{formatMinuten(az)}
              </p>

              {suggestFeierabend ? (
                <>
                  {/* Primary: Feierabend */}
                  <BigButton
                    color={COLORS.red}
                    icon="home"
                    label={t('kiosk.feierabendFrage', { name: vorname })}
                    onClick={() => handleAction('arbeitsende')}
                    disabled={processing}
                  />
                  {/* Secondary: Pausen */}
                  <div style={S.secondaryRow}>
                    <SmallButton
                      color={COLORS.blue}
                      label={t('kiosk.kurzePause', 'Kurze Pause')}
                      enabled={kurzePauseOk}
                      onClick={() => handleAction('kurze_pause')}
                      processing={processing}
                    />
                    <SmallButton
                      color={COLORS.amber}
                      label={t('kiosk.mittagspause', 'Mittagspause')}
                      enabled={mittagOk}
                      onClick={() => handleAction('mittagspause')}
                      processing={processing}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Primary: Pausen gleichwertig */}
                  <div style={S.secondaryRow}>
                    <SmallButton
                      color={COLORS.blue}
                      label={t('kiosk.kurzePause', 'Kurze Pause')}
                      sub={t('kiosk.kurzePauseMax', 'max. 20 Min.')}
                      enabled={kurzePauseOk}
                      disabledReason={scanData.optionen?.kurze_pause?.fehler?.[0]?.text}
                      onClick={() => handleAction('kurze_pause')}
                      processing={processing}
                    />
                    <SmallButton
                      color={COLORS.amber}
                      label={t('kiosk.mittagspause', 'Mittagspause')}
                      sub={t('kiosk.mittagspauseMin', 'mind. 30 Min.')}
                      enabled={mittagOk}
                      disabledReason={scanData.optionen?.mittagspause?.fehler?.[0]?.text}
                      onClick={() => handleAction('mittagspause')}
                      processing={processing}
                    />
                  </div>
                  {/* Secondary: Feierabend */}
                  <BigButton
                    color={COLORS.red}
                    icon="home"
                    label={t('kiosk.arbeitsende', 'Arbeitsende')}
                    onClick={() => handleAction('arbeitsende')}
                    disabled={processing}
                    secondary
                  />
                </>
              )}

              <button onClick={goIdle} style={S.cancelLink}>
                {t('kiosk.abbrechen', 'Abbrechen — eingestempelt bleiben')}
              </button>
            </div>
            <CountdownBar key={countdownKey} duration={countdownMs} />
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════ */}
      {/*  IN_PAUSE — kurze Pause                        */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'in_pause' && scanData && (
        <div style={S.screenWrap} className="kiosk-fade-in" onClick={e => e.target === e.currentTarget && goIdle()}>
          <div style={S.erkannt}>
            <Avatar ma={scanData.mitarbeiter} api={API} />
            <h2 style={S.erkanntName}>{scanData.mitarbeiter?.name}</h2>
            <p style={S.erkanntSub}>
              {t('kiosk.kurzePause', 'Kurze Pause')} — {scanData.pause_dauer_min} Min.
            </p>

            <BigButton
              color={COLORS.green}
              icon="check"
              label={t('kiosk.pauseBeenden', 'Pause beenden')}
              onClick={() => handleAction('pause_ende')}
              disabled={processing}
            />

            <button onClick={goIdle} style={S.cancelLink}>
              {t('kiosk.abbruchPause', 'OK — in Pause bleiben')}
            </button>
          </div>
          <CountdownBar key={countdownKey} duration={countdownMs} />
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  IN_MITTAG — Mittagspause mit Timer             */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'in_mittag' && scanData && (
        <div style={S.screenWrap} className="kiosk-fade-in" onClick={e => e.target === e.currentTarget && goIdle()}>
          <div style={S.erkannt}>
            <Avatar ma={scanData.mitarbeiter} api={API} />
            <h2 style={S.erkanntName}>{scanData.mitarbeiter?.name}</h2>
            <p style={S.erkanntSub}>
              {t('kiosk.mittagspause', 'Mittagspause')} — {scanData.pause_dauer_min || mittagElapsed} Min.
            </p>

            {!kannBeenden ? (
              <>
                <div style={S.warningCard}>
                  <p style={{ fontSize: 'clamp(0.95rem, 1.5vw, 1.2rem)', fontWeight: 500, color: '#854F0B', margin: 0 }}>
                    {t('kiosk.mittagspauseZuKurz', { min: mittagMindest })}
                  </p>
                  <p style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', fontWeight: 600, color: '#633806', margin: '12px 0 0' }}>
                    {t('kiosk.nochWartenMin', { min: nochWartenMin })}
                  </p>
                  <div style={{ height: 6, borderRadius: 3, background: '#FAC775', marginTop: 16, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 3, background: '#d97706',
                      width: `${Math.min(100, (mittagElapsed / mittagMindest) * 100)}%`,
                      transition: 'width 1s ease',
                    }} />
                  </div>
                </div>
                <BigButton
                  color={COLORS.green}
                  icon="check"
                  label={t('kiosk.mittagspauseBeenden', 'Pause beenden')}
                  onClick={() => {}}
                  disabled
                  secondary
                />
              </>
            ) : (
              <>
                <div style={S.successHint}>
                  <p style={{ fontSize: 'clamp(0.95rem, 1.5vw, 1.2rem)', fontWeight: 500, color: '#15803d', margin: 0 }}>
                    {t('kiosk.mittagMinErreicht', 'Mindestdauer erreicht')}
                  </p>
                </div>
                <BigButton
                  color={COLORS.green}
                  icon="check"
                  label={t('kiosk.mittagspauseBeenden', 'Pause beenden')}
                  onClick={() => handleAction('mittag_ende')}
                  disabled={processing}
                />
              </>
            )}

            <button onClick={goIdle} style={S.cancelLink}>
              {t('kiosk.abbruchPause', 'OK — in Pause bleiben')}
            </button>
          </div>
          <CountdownBar key={countdownKey} duration={countdownMs} />
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  BESTAETIGUNG_EIN — Arbeitsbeginn OK            */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'bestaetigung_ein' && scanData && (
        <div style={S.screenWrap} className="kiosk-fade-in" onClick={goIdle}>
          <div style={S.confirm}>
            <div style={S.checkCircle} className="kiosk-check-pop">
              <svg width="clamp(40px, 5vw, 64px)" height="clamp(40px, 5vw, 64px)" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={S.confirmTitle}>
              {t('kiosk.gutenMorgen', { name: vorname })}
            </h2>
            <p style={S.confirmSub}>
              {t('kiosk.eingestempelt', {
                zeit: scanData.zeitpunkt ? formatUhrzeit(scanData.zeitpunkt) : formatUhrzeit(scanData.stempel?.zeitpunkt),
              })}
            </p>

            {scanData.soll_minuten > 0 && (
              <div style={S.infoChip}>
                {t('kiosk.heuteSoll', { stunden: formatSollStunden(scanData.soll_minuten) })}
              </div>
            )}

            {scanData.angepasst && scanData.angepasst_hinweis && (
              <div style={S.warningChip}>{scanData.angepasst_hinweis}</div>
            )}
          </div>
          <CountdownBar key={countdownKey} duration={countdownMs} />
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  BESTAETIGUNG_PAUSE — Pause gestartet/beendet   */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'bestaetigung_pause' && scanData && (
        <div style={S.screenWrap} className="kiosk-fade-in" onClick={goIdle}>
          <div style={S.confirm}>
            <div style={S.checkCircle} className="kiosk-check-pop">
              <svg width="clamp(40px, 5vw, 64px)" height="clamp(40px, 5vw, 64px)" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={S.confirmTitle}>
              {scanData.nachricht || (
                ['kurze_pause', 'mittagspause'].includes(scanData.aktion)
                  ? t('kiosk.pauseGestartet', 'Pause gestartet')
                  : t('kiosk.willkommenZurueck', { name: vorname })
              )}
            </h2>
            {scanData.nachricht_sub && <p style={S.confirmSub}>{scanData.nachricht_sub}</p>}
          </div>
          <CountdownBar key={countdownKey} duration={countdownMs} />
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  FEIERABEND — Arbeitsende + Zusammenfassung     */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'feierabend' && scanData && (
        <div style={S.screenWrap} className="kiosk-fade-in" onClick={goIdle}>
          <div style={S.confirm}>
            <div style={S.waveCircle} className="kiosk-check-pop">
              <svg width="clamp(40px, 5vw, 64px)" height="clamp(40px, 5vw, 64px)" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>
            <h2 style={S.confirmTitle}>
              {t('kiosk.feierabendGruss', { name: vorname })}
            </h2>

            {scanData.tages_zusammenfassung && (
              <div style={S.summaryCard}>
                <SumRow label={t('kiosk.arbeitszeit', 'Arbeitszeit')} value={formatMinuten(scanData.tages_zusammenfassung.brutto_minuten || 0)} />
                {(scanData.tages_zusammenfassung.pausen_min > 0) && (
                  <SumRow label={t('kiosk.pausen', 'Pausen')} value={`- ${formatMinuten(scanData.tages_zusammenfassung.pausen_min)}`} />
                )}
                <div style={S.sumDivider} />
                <SumRow label={t('kiosk.nettoAz', 'Netto-AZ')} value={formatMinuten(scanData.tages_zusammenfassung.netto_minuten || 0)} bold />
                {scanData.tages_zusammenfassung.soll_minuten > 0 ? (
                  <>
                    <SumRow label={t('kiosk.soll', 'SOLL')} value={formatMinuten(scanData.tages_zusammenfassung.soll_minuten)} />
                    <div style={S.sumDivider} />
                    {(() => {
                      const delta = scanData.tages_zusammenfassung.delta_minuten || 0;
                      const neg = delta < 0;
                      return (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                          <span style={S.sumLabel}>{t('kiosk.delta', 'Delta')}</span>
                          <span style={{
                            ...S.sumValue,
                            padding: '2px 10px',
                            borderRadius: 8,
                            background: neg ? '#fee2e2' : '#dcfce7',
                            color: neg ? '#dc2626' : '#15803d',
                            fontWeight: 600,
                          }}>
                            {delta > 0 ? '+' : ''}{formatMinuten(delta)}
                          </span>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    <div style={S.sumDivider} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                      <span style={S.sumLabel}>{t('kiosk.keinArbeitstag', 'Kein Arbeitstag')}</span>
                      <span style={{ ...S.sumValue, padding: '2px 10px', borderRadius: 8, background: '#f3f4f6', color: '#6B7280', fontWeight: 500 }}>
                        {t('kiosk.keinSoll', 'Kein SOLL')}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}

            {(() => {
              // #860 v2.386.0 — 3-stufiges Mittag-Feedback (gelb / orange / rot)
              const status = scanData.mittag_status || (scanData.mittagspause_abgezogen ? 'gelb' : null);
              const hinweis = scanData.mittag_status_hinweis || scanData.mittagspause_hinweis;
              if (!status || !hinweis) return null;
              const chipStyle = status === 'rot'
                ? { background: '#fee2e2', color: '#991b1b' }
                : status === 'orange'
                ? { background: '#ffedd5', color: '#9a3412' }
                : { background: '#fef3c7', color: '#92400e' };
              return (
                <div style={{ ...S.warningChip, ...chipStyle }}>
                  {hinweis}
                </div>
              );
            })()}
          </div>
          <CountdownBar key={countdownKey} duration={countdownMs} />
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  GRUND_EINGABE — Pause vor 2h Arbeitszeit      */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'grund_eingabe' && scanData && (
        <div style={S.screenWrap} className="kiosk-fade-in">
          <div style={S.erkannt}>
            <Avatar ma={scanData.mitarbeiter} api={API} />
            <h2 style={S.erkanntName}>{scanData.mitarbeiter?.name}</h2>
            <p style={{ ...S.erkanntSub, color: '#92400e', fontWeight: 600 }}>
              ⚠️ {t('kiosk.grundEingabe.titel', 'Pause vor Ablauf der Mindestzeit')}
            </p>
            <p style={{ ...S.erkanntSub, marginTop: 4, color: '#4B5563' }}>
              {grundMinuten != null && grundLimit != null
                ? t('kiosk.grundEingabe.subtitle', 'Du arbeitest erst seit {minuten} Minuten. Pause ist erst nach {limit} Min. vorgesehen.')
                    .replace('{minuten}', grundMinuten)
                    .replace('{limit}', grundLimit)
                : grundWarnung}
            </p>
            <p style={{ ...S.erkanntSub, marginTop: 10, fontWeight: 500 }}>
              {t('kiosk.grundEingabe.waehleGrund', 'Bitte wähle einen Grund:')}
            </p>

            {!grundManuellMode ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(10px, 1.2vh, 14px)', width: '100%', maxWidth: 520, marginTop: 'clamp(14px, 1.8vh, 22px)' }}>
                <BigButton
                  color={COLORS.blue}
                  label={t('kiosk.grundEingabe.arzt', 'Arzt / Medizinisch')}
                  onClick={() => submitGrund(t('kiosk.grundEingabe.arzt', 'Arzt / Medizinisch'), 'arzt')}
                  disabled={processing}
                />
                <BigButton
                  color={COLORS.red}
                  label={t('kiosk.grundEingabe.notfall', 'Persönlicher Notfall')}
                  onClick={() => submitGrund(t('kiosk.grundEingabe.notfall', 'Persönlicher Notfall'), 'notfall')}
                  disabled={processing}
                />
                <BigButton
                  color={COLORS.green}
                  label={t('kiosk.grundEingabe.betrieblich', 'Betrieblich angeordnet')}
                  onClick={() => submitGrund(t('kiosk.grundEingabe.betrieblich', 'Betrieblich angeordnet'), 'betrieblich')}
                  disabled={processing}
                />
                <BigButton
                  color={COLORS.amber}
                  label={t('kiosk.grundEingabe.sonstiges', 'Sonstiges')}
                  onClick={() => setGrundManuellMode(true)}
                  disabled={processing}
                  secondary
                />
              </div>
            ) : (
              <div style={{ width: '100%', maxWidth: 520, marginTop: 'clamp(14px, 1.8vh, 22px)' }}>
                <input
                  type="text"
                  autoFocus
                  value={grundManuell}
                  onChange={(e) => setGrundManuell(e.target.value)}
                  placeholder={t('kiosk.grundEingabe.sonstigesPlaceholder', 'Mindestens 5 Zeichen...')}
                  maxLength={200}
                  style={{
                    width: '100%',
                    padding: 'clamp(14px, 1.8vh, 20px)',
                    fontSize: 'clamp(1rem, 1.5vw, 1.2rem)',
                    border: '2px solid #E5E7EB',
                    borderRadius: 12,
                    outline: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#338edc')}
                  onBlur={(e) => (e.target.style.borderColor = '#E5E7EB')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && grundManuell.trim().length >= 5) {
                      submitGrund(`Sonstiges: ${grundManuell.trim()}`, 'sonstiges');
                    }
                  }}
                />
                <div style={{ display: 'flex', gap: 12, marginTop: 14 }}>
                  <SmallButton
                    color={COLORS.amber}
                    label={t('kiosk.zurueck', 'Zurück')}
                    enabled={!processing}
                    onClick={() => { setGrundManuellMode(false); setGrundManuell(''); }}
                    processing={false}
                  />
                  <SmallButton
                    color={COLORS.blue}
                    label={t('kiosk.grundEingabe.bestaetigen', 'Bestätigen')}
                    enabled={grundManuell.trim().length >= 5 && !processing}
                    onClick={() => submitGrund(`Sonstiges: ${grundManuell.trim()}`, 'sonstiges')}
                    processing={processing}
                  />
                </div>
              </div>
            )}

            <button onClick={cancelGrund} style={S.cancelLink}>
              {t('kiosk.grundEingabe.abbrechen', 'Abbrechen — zurück')}
            </button>
          </div>
          <CountdownBar key={countdownKey} duration={countdownMs} />
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  REGEL_BLOCK — Pause/Aktion blockiert (#817)   */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'regel_block' && regelBlock && (
        <div style={S.screenWrap} className="kiosk-fade-in" onClick={goIdle}>
          <div style={S.confirm}>
            <div style={{ ...S.errorCircle, background: '#d97706' }} className="kiosk-check-pop">
              <svg width="clamp(40px, 5vw, 64px)" height="clamp(40px, 5vw, 64px)" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h2 style={{ ...S.confirmTitle, color: '#92400e' }}>
              {t('kiosk.regel.titel', 'Nicht möglich')}
            </h2>
            <p style={{ ...S.confirmSub, maxWidth: 520, padding: '0 clamp(12px, 2vw, 24px)' }}>
              {(() => {
                const d = regelBlock.details?.[0] || {};
                switch (regelBlock.code) {
                  case 'MAX_ANZAHL':
                    return t('kiosk.regel.maxPausenErreicht', 'Max. {max} Kurzpausen pro Tag bereits erreicht.').replace('{max}', d.max ?? '');
                  case 'ABSTAND_ZU_KURZ':
                    return t('kiosk.regel.pauseAbstandZuKurz', 'Mind. {min} Min. Abstand zwischen Pausen. Letzte Pause vor {aktuell} Min.').replace('{min}', d.min ?? '').replace('{aktuell}', d.aktuell ?? '');
                  case 'BEREITS_MITTAG':
                    return t('kiosk.regel.bereitsMittag', 'Mittagspause bereits genommen.');
                  case 'MITTAG_ZU_KURZ':
                    return t('kiosk.regel.mittagZuKurz', 'Mittagspause dauert noch. Mind. {min} Min. (aktuell: {aktuell} Min.).').replace('{min}', d.min ?? '').replace('{aktuell}', d.aktuell ?? '');
                  case 'KEINE_PAUSE':
                    return t('kiosk.regel.keinePause', 'Keine laufende Pause.');
                  case 'BEREITS_EINGESTEMPELT':
                    return t('kiosk.regel.bereitsEingestempelt', 'Bereits eingestempelt.');
                  case 'NICHT_EINGESTEMPELT':
                    return t('kiosk.regel.nichtEingestempelt', 'Nicht eingestempelt.');
                  default:
                    return regelBlock.meldung || t('kiosk.regel.generisch', 'Aktion nicht erlaubt.');
                }
              })()}
            </p>
            <p style={{ ...S.confirmSub, fontSize: 'clamp(0.85rem, 1.2vw, 1rem)', color: '#9CA3AF', marginTop: 16 }}>
              {t('kiosk.regel.tipp', 'Tippen um fortzufahren')}
            </p>
          </div>
          <CountdownBar key={countdownKey} duration={countdownMs} />
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  OFFLINE_AUSWAHL — kein Netz beim Scan (#1817) */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'offline_auswahl' && (
        <div style={S.screenWrap} className="kiosk-fade-in" onClick={e => e.target === e.currentTarget && goIdle()}>
          <div style={S.erkannt}>
            <div style={{ ...S.errorCircle, background: COLORS.amber.bg }} className="kiosk-check-pop">
              <svg width="clamp(40px, 5vw, 64px)" height="clamp(40px, 5vw, 64px)" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M5 12.5a10 10 0 0 1 5.2-2.4M10.7 5.1a16 16 0 0 1 10.4 4.2M2 8.8a16 16 0 0 1 4.2-2.6" />
                <line x1="12" y1="20" x2="12.01" y2="20" />
              </svg>
            </div>
            <h2 style={{ ...S.erkanntName, color: '#92400e' }}>
              {t('kiosk.offline_titel', 'Keine Verbindung')}
            </h2>
            <p style={S.erkanntSub}>
              {t('kiosk.offline_frage', 'Bitte auswählen — der Stempel wird mit der jetzigen Uhrzeit nachgeholt, sobald das Netz wieder da ist.')}
            </p>

            <BigButton
              color={COLORS.green}
              icon="check"
              label={t('kiosk.arbeitsbeginn')}
              onClick={() => offlineBuchen('arbeit_start')}
            />
            <BigButton
              color={COLORS.red}
              icon="home"
              label={t('kiosk.arbeitsende', 'Arbeitsende')}
              onClick={() => offlineBuchen('arbeitsende')}
            />

            <button onClick={goIdle} style={S.cancelLink}>
              {t('kiosk.abbruch', 'Abbrechen')}
            </button>
          </div>
          <CountdownBar key={countdownKey} duration={countdownMs} />
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/*  FEHLER                                        */}
      {/* ═══════════════════════════════════════════════ */}
      {state === 'fehler' && (
        <div style={S.screenWrap} className="kiosk-fade-in" onClick={goIdle}>
          <div style={S.confirm}>
            <div style={S.errorCircle} className="kiosk-check-pop">
              <svg width="clamp(40px, 5vw, 64px)" height="clamp(40px, 5vw, 64px)" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <h2 style={{ ...S.confirmTitle, color: '#dc2626' }}>
              {error || t('kiosk.fehler', 'Fehler')}
            </h2>
          </div>
          <CountdownBar key={countdownKey} duration={countdownMs} />
        </div>
      )}

      {/* Offline queue indicator — #1817: ein abgelehnter Eintrag wird NICHT
          geloescht, also muss er auch sichtbar sein (AK5). Sonst laege
          Arbeitszeit unbemerkt im localStorage eines Tablets. */}
      {(queueStatus.offen > 0 || queueStatus.abgelehnt > 0) && (
        <div style={{
          ...S.queueBadge,
          ...(queueStatus.abgelehnt > 0
            ? { background: COLORS.red.light, color: '#991b1b', fontWeight: 600 }
            : null),
        }}>
          {queueStatus.abgelehnt > 0
            ? t('kiosk.queue_abgelehnt', '{n} Stempel nicht buchbar — bitte HR melden')
                .replace('{n}', queueStatus.abgelehnt)
            : `${queueStatus.offen} ${t('kiosk.queued', 'in Warteschlange')}`}
        </div>
      )}

      {/* ─── CSS ─── */}
      <style>{`
        @keyframes kioskFadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .kiosk-fade-in { animation: kioskFadeIn 0.3s ease-out; }

        @keyframes kioskPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(51,142,220,0.25); }
          50% { box-shadow: 0 0 0 clamp(12px, 2vw, 24px) rgba(51,142,220,0); }
        }
        .kiosk-pulse { animation: kioskPulse 2.5s ease-in-out infinite; }

        @keyframes kioskCheckPop {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.12); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .kiosk-check-pop { animation: kioskCheckPop 0.5s ease-out; }

        @keyframes kioskCountdown {
          from { transform: scaleX(1); }
          to { transform: scaleX(0); }
        }

        @keyframes kioskShake {
          0%,100% { transform: translateX(0); }
          20%,60% { transform: translateX(-8px); }
          40%,80% { transform: translateX(8px); }
        }
        .kiosk-shake { animation: kioskShake 0.5s ease-in-out; }

        /* Remove tap highlight on mobile */
        * { -webkit-tap-highlight-color: transparent; }
      `}</style>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════
//  SUB-COMPONENTS
// ════════════════════════════════════════════════════════════════

function Avatar({ ma, api }) {
  const name = ma?.name || '?';
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2);
  const idx = (name.charCodeAt(0) || 0) % AVATAR_BG.length;
  const size = 'clamp(80px, 12vw, 140px)';

  if (ma?.foto_url) {
    return (
      <img
        src={`${api}${ma.foto_url}`}
        alt=""
        style={{
          width: size, height: size, borderRadius: '50%',
          objectFit: 'cover', border: '3px solid #E5E7EB',
        }}
      />
    );
  }

  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: AVATAR_BG[idx], color: AVATAR_FG[idx],
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 'clamp(1.8rem, 4vw, 3.5rem)', fontWeight: 700,
      flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function BigButton({ color, icon, label, onClick, disabled, secondary }) {
  const height = secondary ? 'clamp(56px, 7vh, 80px)' : 'clamp(70px, 9vh, 110px)';
  const fs = secondary ? 'clamp(1rem, 1.6vw, 1.3rem)' : 'clamp(1.2rem, 2vw, 1.6rem)';

  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        width: '100%',
        maxWidth: 520,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'clamp(10px, 1.5vw, 16px)',
        background: secondary ? color.light : color.bg,
        color: secondary ? color.bg : color.text,
        border: `2px solid ${secondary ? color.border : color.bg}`,
        borderRadius: 'clamp(12px, 1.5vw, 20px)',
        padding: '0 clamp(16px, 2vw, 32px)',
        minHeight: height,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.35 : 1,
        fontSize: fs,
        fontWeight: 600,
        fontFamily: 'inherit',
        transition: 'all 0.15s',
        marginTop: 'clamp(12px, 1.5vh, 20px)',
      }}
    >
      {icon === 'check' && (
        <svg width="clamp(24px, 3vw, 36px)" height="clamp(24px, 3vw, 36px)" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
      {icon === 'home' && (
        <svg width="clamp(24px, 3vw, 36px)" height="clamp(24px, 3vw, 36px)" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 0 0 1 1h3m10-11l2 2m-2-2v10a1 1 0 0 1-1 1h-3m-4 0a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-2z" />
        </svg>
      )}
      {label}
    </button>
  );
}

function SmallButton({ color, label, sub, enabled = true, disabledReason, onClick, processing }) {
  const disabled = !enabled || processing;
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        flex: 1,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: 'white',
        border: `2px solid ${enabled ? color.border : '#E5E7EB'}`,
        borderRadius: 'clamp(12px, 1.5vw, 20px)',
        padding: 'clamp(16px, 2vh, 28px) clamp(12px, 1.5vw, 20px)',
        minHeight: 'clamp(80px, 10vh, 120px)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.35 : 1,
        transition: 'all 0.15s',
        fontFamily: 'inherit',
      }}
    >
      <span style={{
        fontSize: 'clamp(1rem, 1.6vw, 1.3rem)',
        fontWeight: 600,
        color: enabled ? color.bg : '#9CA3AF',
      }}>
        {label}
      </span>
      {enabled && sub && (
        <span style={{ fontSize: 'clamp(0.75rem, 1vw, 0.9rem)', color: '#6B7280', marginTop: 4 }}>
          {sub}
        </span>
      )}
      {!enabled && disabledReason && (
        <span style={{ fontSize: 'clamp(0.7rem, 0.9vw, 0.85rem)', color: '#dc2626', marginTop: 4 }}>
          {disabledReason}
        </span>
      )}
    </button>
  );
}

function CountdownBar({ duration }) {
  if (!duration) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, height: 'clamp(4px, 0.5vh, 6px)',
      background: 'rgba(51, 142, 220, 0.15)',
    }}>
      <div style={{
        height: '100%', background: '#338edc', transformOrigin: 'left',
        animation: `kioskCountdown ${duration}ms linear forwards`,
      }} />
    </div>
  );
}

function SumRow({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
      <span style={bold ? { ...S.sumLabel, fontWeight: 600 } : S.sumLabel}>{label}</span>
      <span style={bold ? { ...S.sumValue, fontWeight: 600 } : S.sumValue}>{value}</span>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════
//  STYLES (inline object for kiosk isolation)
// ════════════════════════════════════════════════════════════════
const S = {
  root: {
    minHeight: '100vh',
    maxHeight: '100vh',
    overflow: 'hidden',
    background: 'linear-gradient(150deg, #D6E9FA 0%, #EBF4FD 30%, #F5F6FB 60%, #FFFFFF 100%)',
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'manipulation',
  },

  // ─── Screens ───
  screenWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: 'clamp(16px, 3vw, 40px)',
    position: 'relative',
  },


  // ─── Logo ───
  logo: {
    position: 'absolute',
    top: 'clamp(16px, 2vh, 28px)',
    left: 'clamp(20px, 3vw, 40px)',
  },

  // ─── Clock ───
  clockWrap: {
    textAlign: 'center',
    marginBottom: 'clamp(24px, 4vh, 48px)',
  },
  clockTime: {
    fontSize: 'clamp(5rem, 11vw, 13rem)',
    fontWeight: 300,
    color: '#1A1A2E',
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.02em',
    lineHeight: 1,
  },
  clockSeconds: {
    fontSize: 'clamp(2rem, 4vw, 4rem)',
    fontWeight: 300,
    color: '#9CA3AF',
    marginLeft: 'clamp(2px, 0.3vw, 6px)',
  },
  clockDate: {
    fontSize: 'clamp(1.1rem, 2vw, 1.6rem)',
    color: '#6B7280',
    marginTop: 'clamp(4px, 0.5vh, 12px)',
    fontWeight: 400,
  },

  // ─── Scan prompt ───
  scanPrompt: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'clamp(8px, 1vh, 16px)',
    marginBottom: 'clamp(20px, 3vh, 40px)',
  },
  scanIconRing: {
    width: 'clamp(64px, 8vw, 100px)',
    height: 'clamp(64px, 8vw, 100px)',
    borderRadius: '50%',
    background: 'rgba(51, 142, 220, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scanText: {
    fontSize: 'clamp(1.1rem, 1.8vw, 1.5rem)',
    color: '#4B5563',
    fontWeight: 500,
    margin: 0,
  },
  scanSub: {
    fontSize: 'clamp(0.85rem, 1.2vw, 1.1rem)',
    color: '#9CA3AF',
    margin: 0,
  },

  // ─── Anwesend ───
  anwesendBadge: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 'clamp(6px, 0.8vw, 10px)',
    marginBottom: 'clamp(20px, 3vh, 40px)',
  },
  anwesendNumber: {
    fontSize: 'clamp(1.8rem, 3.5vw, 3rem)',
    fontWeight: 700,
    color: '#1A1A2E',
    fontVariantNumeric: 'tabular-nums',
  },
  anwesendLabel: {
    fontSize: 'clamp(0.9rem, 1.4vw, 1.2rem)',
    color: '#6B7280',
  },

  // ─── Bottom bar ───
  bottomBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    padding: 'clamp(12px, 1.5vh, 20px) clamp(20px, 3vw, 40px)',
  },
  bottomBtn: {
    fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
    color: '#6B7280',
    background: 'rgba(255,255,255,0.7)',
    border: '1px solid #E5E7EB',
    borderRadius: 10,
    padding: 'clamp(6px, 0.8vh, 10px) clamp(12px, 1.5vw, 20px)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  // ─── PIN ───
  pinWrap: {
    textAlign: 'center',
    maxWidth: 'clamp(300px, 40vw, 420px)',
    width: '100%',
  },
  pinTitle: {
    fontSize: 'clamp(1.5rem, 2.5vw, 2.2rem)',
    fontWeight: 500,
    color: '#1A1A2E',
    margin: '0 0 8px',
  },
  pinHint: {
    fontSize: 'clamp(0.9rem, 1.3vw, 1.1rem)',
    color: '#6B7280',
    margin: '0 0 clamp(16px, 2vh, 28px)',
  },
  pinDots: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'clamp(14px, 2vw, 24px)',
    marginBottom: 'clamp(16px, 2vh, 28px)',
  },
  pinDot: {
    width: 'clamp(16px, 2.2vw, 24px)',
    height: 'clamp(16px, 2.2vw, 24px)',
    borderRadius: '50%',
    transition: 'all 0.15s',
  },
  pinError: {
    fontSize: 'clamp(0.85rem, 1.2vw, 1rem)',
    color: '#dc2626',
    fontWeight: 500,
    marginBottom: 16,
    // #1863 — Der Grund steht jetzt als Satz hier und nicht mehr als drei
    // Woerter. Ohne Breitenbegrenzung und Umbruch schoebe er das Nummernfeld
    // aus dem Bild; ein abgeschnittener Hinweis waere so unbrauchbar wie der
    // falsche vorher.
    maxWidth: 'min(90vw, 420px)',
    lineHeight: 1.35,
    textAlign: 'center',
  },
  numpad: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 'clamp(6px, 0.8vw, 12px)',
    marginBottom: 12,
  },
  entsperrBtn: {
    width: '100%',
    height: 'clamp(56px, 7vh, 80px)',
    borderRadius: 'clamp(12px, 1.5vw, 18px)',
    fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
    fontWeight: 600,
    background: 'white',
    color: '#1A1A2E',
    border: '1px solid #E5E7EB',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  entsperrZurueck: {
    marginTop: 'clamp(12px, 2vh, 20px)',
    background: 'none',
    border: 'none',
    color: '#6B7280',
    fontSize: 'clamp(0.8rem, 1.1vw, 0.95rem)',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  numBtn: {
    height: 'clamp(56px, 7vh, 80px)',
    borderRadius: 'clamp(12px, 1.5vw, 18px)',
    fontSize: 'clamp(1.3rem, 2vw, 1.8rem)',
    fontWeight: 600,
    background: 'white',
    color: '#1A1A2E',
    border: '1px solid #E5E7EB',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.1s',
  },

  // ─── Erkannt ───
  erkannt: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 'clamp(400px, 50vw, 560px)',
    width: '100%',
    textAlign: 'center',
  },
  erkanntName: {
    fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
    fontWeight: 600,
    color: '#1A1A2E',
    margin: 'clamp(12px, 1.5vh, 20px) 0 4px',
  },
  erkanntGreeting: {
    fontSize: 'clamp(1rem, 1.6vw, 1.3rem)',
    color: '#15803d',
    fontWeight: 500,
    margin: '0 0 clamp(8px, 1vh, 16px)',
  },
  erkanntSub: {
    fontSize: 'clamp(0.95rem, 1.5vw, 1.2rem)',
    color: '#6B7280',
    margin: '0 0 clamp(8px, 1vh, 16px)',
  },
  secondaryRow: {
    display: 'flex',
    gap: 'clamp(10px, 1.5vw, 16px)',
    width: '100%',
    maxWidth: 520,
    marginTop: 'clamp(12px, 1.5vh, 20px)',
  },

  // ─── Cancel link ───
  cancelLink: {
    fontSize: 'clamp(0.85rem, 1.2vw, 1rem)',
    color: '#6B7280',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px 0',
    marginTop: 'clamp(12px, 1.5vh, 20px)',
    fontFamily: 'inherit',
  },

  // ─── Confirm ───
  confirm: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 'clamp(360px, 45vw, 520px)',
    width: '100%',
    cursor: 'pointer',
  },
  checkCircle: {
    width: 'clamp(80px, 10vw, 120px)',
    height: 'clamp(80px, 10vw, 120px)',
    borderRadius: '50%',
    background: '#15803d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 'clamp(16px, 2vh, 28px)',
  },
  waveCircle: {
    width: 'clamp(80px, 10vw, 120px)',
    height: 'clamp(80px, 10vw, 120px)',
    borderRadius: '50%',
    background: '#fee2e2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 'clamp(16px, 2vh, 28px)',
  },
  errorCircle: {
    width: 'clamp(80px, 10vw, 120px)',
    height: 'clamp(80px, 10vw, 120px)',
    borderRadius: '50%',
    background: '#dc2626',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 'clamp(16px, 2vh, 28px)',
  },
  confirmTitle: {
    fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
    fontWeight: 500,
    color: '#1A1A2E',
    margin: '0 0 8px',
  },
  confirmSub: {
    fontSize: 'clamp(1rem, 1.6vw, 1.3rem)',
    color: '#6B7280',
    margin: 0,
  },

  // ─── Info chips ───
  infoChip: {
    background: '#EBF4FD',
    color: '#0C447C',
    padding: 'clamp(8px, 1vh, 12px) clamp(16px, 2vw, 24px)',
    borderRadius: 10,
    fontSize: 'clamp(0.9rem, 1.3vw, 1.1rem)',
    fontWeight: 500,
    marginTop: 'clamp(12px, 1.5vh, 20px)',
  },
  warningChip: {
    background: '#fef3c7',
    color: '#92400e',
    padding: 'clamp(8px, 1vh, 12px) clamp(16px, 2vw, 24px)',
    borderRadius: 10,
    fontSize: 'clamp(0.85rem, 1.2vw, 1rem)',
    fontWeight: 500,
    marginTop: 'clamp(12px, 1.5vh, 20px)',
  },

  // ─── Warning/Success cards ───
  warningCard: {
    background: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: 16,
    padding: 'clamp(16px, 2vh, 28px) clamp(20px, 2.5vw, 36px)',
    maxWidth: 480,
    width: '100%',
    textAlign: 'center',
    marginTop: 'clamp(12px, 1.5vh, 20px)',
  },
  successHint: {
    background: '#dcfce7',
    border: '1px solid #86efac',
    borderRadius: 12,
    padding: 'clamp(12px, 1.5vh, 20px) clamp(20px, 2.5vw, 32px)',
    maxWidth: 440,
    width: '100%',
    textAlign: 'center',
    marginTop: 'clamp(12px, 1.5vh, 20px)',
  },

  // ─── Summary card (Feierabend) ───
  summaryCard: {
    background: 'white',
    borderRadius: 16,
    border: '1px solid #E5E7EB',
    padding: 'clamp(16px, 2vh, 28px) clamp(20px, 2.5vw, 36px)',
    textAlign: 'left',
    maxWidth: 420,
    width: '100%',
    marginTop: 'clamp(16px, 2vh, 24px)',
  },
  sumDivider: {
    borderTop: '1px solid #E5E7EB',
    margin: '6px 0',
  },
  sumLabel: {
    fontSize: 'clamp(0.85rem, 1.2vw, 1.05rem)',
    color: '#6B7280',
    fontWeight: 400,
  },
  sumValue: {
    fontSize: 'clamp(0.85rem, 1.2vw, 1.05rem)',
    color: '#1A1A2E',
    fontWeight: 400,
    fontVariantNumeric: 'tabular-nums',
  },

  // ─── Queue badge ───
  queueBadge: {
    position: 'fixed',
    bottom: 'clamp(12px, 1.5vh, 20px)',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: 12,
    padding: '4px 12px',
    borderRadius: 99,
    background: '#fef3c7',
    color: '#854F0B',
  },
};
